/* Energy Unleashed — cart drawer.
 *
 * Replaces Upcart, which reconciled every /cart/add.js against a cart Liquid
 * preloaded at page render and destroyed multi-line bundle adds in the process.
 *
 * Exposes window.EUCart so the bundle selector has a real API to call instead
 * of guessing at a third party's globals:
 *
 *   EUCart.open()             open the drawer
 *   EUCart.close()
 *   EUCart.refresh()          re-read /cart.js and repaint  -> Promise<cart>
 *   EUCart.openWith(cart)     paint a cart we already have, then open
 */
(function () {
  'use strict';

  var ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var SHOP_URL = '/collections/all';
  var url = function (p) { return ROOT.replace(/\/?$/, '/') + p; };

  /* The same generator eu-bundle-selector.js uses. Needed here because a cart
   * can now become a bundle without ever having passed through the selector:
   * every quick add from a collection page lands as a loose tin, and
   * completionStep is what offers to group them. */
  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'eu-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  /* Reward labels are written as imperatives for the progress pips ("Choose A
   * Free Tin"), which reads badly mid-sentence: "away from choose a free tin".
   * Drop the leading verb so the sentence works. */
  function phrase(label) {
    return String(label || '').toLowerCase().replace(/^(choose|get|unlock|claim)\s+/, '');
  }

  /* "free" is the word doing the work in every reward name, so it carries the
   * weight. Escaped first, then the tag goes in — never the other way round. */
  /* "Free Standard Delivery" under a pip wraps to three lines on a phone.
   * "Standard" fits on one and still names the milestone, which beats hiding
   * the labels entirely and leaving three bare dollar amounts. */
  function shortLabel(label) {
    var l = String(label || '');
    if (/express/i.test(l)) return 'Express';
    if (/standard/i.test(l)) return 'Standard';
    if (/tin/i.test(l)) return 'Free Tin';
    return l;
  }

  function shout(label) {
    // Keep the label's own capitalisation; only FREE is shouted.
    return esc(String(label || '').replace(/^(Choose|Get|Unlock|Claim)\s+/i, ''))
      .replace(/\bfree\b/gi, '<b>FREE</b>');
  }

  /* Meta Pixel.
   *
   * The Meta sales channel is already installed and fires the standard events
   * (PageView, ViewContent, AddToCart, InitiateCheckout, Purchase) through
   * Shopify's own web pixel. Everything emitted here is therefore a CUSTOM
   * event — firing our own AddToCart would double-count the funnel and quietly
   * corrupt the ad reporting these numbers feed.
   *
   * No pixel is initialised and no id is hard-coded: if fbq isn't on the page
   * this is a no-op. The same payload is pushed to dataLayer so GA4 or GTM can
   * read it later without a second integration. */
  function track(event, params) {
    var payload = params || {};
    try { if (typeof window.fbq === 'function') window.fbq('trackCustom', event, payload); }
    catch (e) {}
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: event }, payload)); }
    catch (e) {}
  }

  /* Same estimate the product page prints, from the same settings, so the two
   * can never disagree about when a parcel lands. Weekends only — holidays
   * would need a calendar we do not have. */
  function addBusinessDays(from, n) {
    var d = new Date(from.getTime());
    while (n > 0) { d.setDate(d.getDate() + 1); var w = d.getDay(); if (w !== 0 && w !== 6) n--; }
    return d;
  }
  function etaLabel(days) {
    if (!days) return '';
    return addBusinessDays(new Date(), days)
      .toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'long' });
  }

  /* Same bands the product page uses — derived from the real mg spread across
   * the catalogue, not an invented score. Duplicated rather than shared because
   * the two files load independently; the thresholds are the contract. */
  function strengthBand(mg) {
    if (!mg) return null;
    if (mg < 77) return { label: 'Mild', level: 1 };
    if (mg < 100) return { label: 'Medium', level: 2 };
    if (mg < 130) return { label: 'Strong', level: 3 };
    return { label: 'Extra Strong', level: 4 };
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  var FORMAT = '${{amount}}';
  function money(cents, whole) {
    var v = (Number(cents) / 100).toFixed(2);
    // "+$80" reads cleaner than "+$80.00" on a button, but only when exact.
    if (whole && Number(cents) % 100 === 0) v = String(Number(cents) / 100);
    if (FORMAT.indexOf('{{') !== -1) {
      return FORMAT
        .replace(/\{\{\s*amount\s*\}\}/, v)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)));
    }
    return '$' + v;
  }

  function json(path, opts) {
    return fetch(url(path), Object.assign({
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
    }, opts || {})).then(function (r) {
      return r.text().then(function (raw) {
        var body = null;
        try { body = JSON.parse(raw); } catch (e) {}
        if (!r.ok) throw new Error((body && (body.description || body.message)) || ('HTTP ' + r.status));
        return body;
      });
    });
  }

  var Cart = {
    el: null,
    cart: null,
    // Which bundle groups the customer has expanded. Collapsed is the default.
    _openGroups: {},

    mount: function () {
      this.el = document.querySelector('[data-eu-cart]');
      if (!this.el) return;
      FORMAT = this.el.getAttribute('data-money-format') || FORMAT;

      try { this.tiers = JSON.parse(this.el.getAttribute('data-rewards') || '[]'); }
      catch (e) { this.tiers = []; }
      this.rewardsOn = this.el.getAttribute('data-rewards-on') !== 'false';
      this.completeMsg = this.el.getAttribute('data-rewards-complete') || "You've unlocked every cart reward.";
      this.giftAllow = (this.el.getAttribute('data-gift-allowlist') || '')
        .split(',').map(function (s) { return s.trim(); }).filter(Boolean);

      this.upsellOn = this.el.getAttribute('data-upsell-on') !== 'false';
      this.upsellTitle = this.el.getAttribute('data-upsell-title') || 'Add another flavour';
      this.upsellCount = parseInt(this.el.getAttribute('data-upsell-count'), 10) || 3;
      this.upsellHandles = (this.el.getAttribute('data-upsell-handles') || '')
        .split(/[,\n]/).map(function (s) { return s.trim(); }).filter(Boolean);

      this.protectionOn = this.el.getAttribute('data-protection-on') !== 'false'
        && this.el.getAttribute('data-protection-available') === 'true';
      this.protectionId = this.el.getAttribute('data-protection-id') || '';
      this.protectionPrice = parseInt(this.el.getAttribute('data-protection-price'), 10) || 0;
      this.protectionBlurb = this.el.getAttribute('data-protection-blurb') || '';
      this.inline = this.el.getAttribute('data-inline') === 'true';
      this.etaStandard = parseInt(this.el.getAttribute('data-eta-standard'), 10) || 0;
      this.etaExpress = parseInt(this.el.getAttribute('data-eta-express'), 10) || 0;

      var self = this;

      this.el.addEventListener('click', function (e) {
        if (e.target.closest('[data-eu-cart-close]') || e.target === self.el.querySelector('.eu-cart__scrim')) {
          self.close(); return;
        }
        var step = e.target.closest('[data-eu-step]');
        if (step) {
          self.change(step.getAttribute('data-eu-key'), parseInt(step.getAttribute('data-eu-step'), 10));
          return;
        }
        var rm = e.target.closest('[data-eu-remove]');
        if (rm) { self.setQty(rm.getAttribute('data-eu-remove'), 0); return; }

        var tog = e.target.closest('[data-eu-group-toggle]');
        if (tog) {
          var gid = tog.getAttribute('data-eu-group-toggle');
          self._openGroups[gid] = !self._openGroups[gid];
          if (self.cart) self.render(self.cart);
          return;
        }

        var rmGroup = e.target.closest('[data-eu-remove-group]');
        if (rmGroup) { self.removeGroup(rmGroup.getAttribute('data-eu-remove-group')); return; }

        if (e.target.closest('[data-eu-gift-open]')) { self.openGiftPicker(); return; }
        if (e.target.closest('[data-eu-gift-close]')) { self.closeGiftPicker(); return; }
        var pinc = e.target.closest('[data-eu-pick-inc]');
        if (pinc) { self.picked(pinc.getAttribute('data-eu-pick-inc')); return; }
        var pdec = e.target.closest('[data-eu-pick-dec]');
        if (pdec) { self.unpick(pdec.getAttribute('data-eu-pick-dec')); return; }
        var pacc = e.target.closest('[data-eu-pick-acc]');
        if (pacc) {
          var k = pacc.getAttribute('data-eu-pick-acc');
          self._pbrand = self._pbrand === k ? null : k;
          self.paintPicker();
          return;
        }
        if (e.target.closest('[data-eu-pick-done]')) { self.closeGiftPicker(); return; }

        var pick = e.target.closest('[data-eu-gift-pick]');
        if (pick && pick.getAttribute('data-disabled') !== 'true') {
          self.picked(pick.getAttribute('data-eu-gift-pick')); return;
        }
        var next = e.target.closest('[data-eu-next]');
        if (next) { self.startNextStep(); return; }
        var ups = e.target.closest('[data-eu-upsell-add]');
        if (ups) { self.addUpsell(ups.getAttribute('data-eu-upsell-add')); return; }

        var chGift = e.target.closest('[data-eu-gift-change]');
        if (chGift) { self.setQty(chGift.getAttribute('data-eu-gift-change'), 0).then(function () { self.openGiftPicker(); }); return; }
        var rmGift = e.target.closest('[data-eu-gift-remove]');
        if (rmGift) { /* removing a gift usually means "wrong flavour", not "no thanks" — leave the prompt up so it can be re-picked (QA, 2026-08-29) */ self.setQty(rmGift.getAttribute('data-eu-gift-remove'), 0); return; }
      });

      /* Repaint on search without touching the field, so typing never costs it
       * focus — same reason as the product page. */
      this.el.addEventListener('input', function (e) {
        if (!e.target.matches('[data-eu-pick-search]')) return;
        self._pq = e.target.value.trim();
        var body = self.el.querySelector('[data-eu-gift-modal] .eu-modal__body');
        if (!body) return self.paintPicker();
        var q = self._pq.toLowerCase();
        if (!q) return self.paintPicker();
        var hits = self.pickerPool().filter(function (t) {
          return (t.flavour + ' ' + t.brand + ' ' + (t.notes || '')).toLowerCase().indexOf(q) !== -1;
        });
        body.innerHTML = hits.length
          ? '<div class="eu-fgrid">' + hits.map(function (t) { return self.pickerCard(t); }).join('') + '</div>'
          : '<p class="eu-modal__none"><b>No flavours found</b>Try another flavour or brand.</p>';
      });

      this.el.addEventListener('change', function (e) {
        if (e.target.matches('[data-eu-protection-toggle]')) self.toggleProtection(e.target.checked);
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.isOpen()) self.close();
      });

      /* The free-tin bar is pinned above the scrolling list. Without an edge, a
       * card scrolled under it looked like it overlapped the bar (Matthew,
       * 27 Oct 2026), so the list gets a divider and shadow once it scrolls.
       * Capture phase: scroll events don't bubble, and the body is re-rendered. */
      this.el.addEventListener('scroll', function (e) {
        if (!e.target.classList || !e.target.classList.contains('eu-cart__body')) return;
        e.target.setAttribute('data-eu-scrolled', e.target.scrollTop > 2 ? 'true' : 'false');
      }, true);

      // Take over every route to the cart page.
      document.addEventListener('click', function (e) {
        var a = e.target.closest('a[href$="/cart"], a[href*="/cart?"], [data-eu-cart-open]');
        if (!a) return;
        e.preventDefault();
        if (self.inline) { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
        self.refresh().then(function () { self.open(); });
      });

      /* The header cart button is Horizon's, and carries a declarative
       * `on:click="#cart-drawer/toggle"`. Capture phase, so this runs before
       * that binding and before any component listener on the button itself —
       * bubbling would be too late if Horizon called stopPropagation. */
      document.addEventListener('click', function (e) {
        var t = e.target.closest('[data-testid="cart-drawer-trigger"], [aria-controls="cart-drawer"]');
        if (!t) return;
        e.preventDefault();
        e.stopPropagation();
        self.refresh().then(function () { self.open(); });
      }, true);

      /* Anything else that adds to the cart — Horizon quick-add, the theme's own
       * product form on the premade-bundle pages — announces itself with the
       * standard storefront event. Our own mutations don't fire it, so this
       * can't loop. Not on the cart page, where editing a line should not throw
       * a drawer over the top.
       *
       * The event is dispatched OPTIMISTICALLY. Horizon builds the form data,
       * fires this, and only then runs the /cart/add fetch — resolving
       * `event.promise` with the real cart once the server answers. Refreshing
       * the moment the event lands therefore reads the cart as it was BEFORE the
       * add, so the drawer opened without the thing that had just been added.
       * That is why the premade bundles looked like they never reached the cart:
       * they had, and the drawer was showing a stale snapshot.
       *
       * So wait for the promise. Fall back to refreshing immediately if some
       * other dispatcher sends the event without one.
       *
       * Only `add` re-opens the drawer. A removal or a quantity change elsewhere
       * should update what we hold without throwing the panel open again. */
      document.addEventListener('shopify:cart:lines-update', function (e) {
        if (/\/cart\/?$/.test(window.location.pathname)) return;

        var isAdd = !e || e.action == null || e.action === 'add';
        var settle = function (res) {
          // A failed add still resolves; don't open a drawer over an error.
          if (res && res.detail && res.detail.didError) return;
          self.refresh().then(function () { if (isAdd) self.open(); });
        };

        if (e && e.promise && typeof e.promise.then === 'function') {
          e.promise.then(settle).catch(function () { /* add failed — leave it */ });
        } else {
          settle();
        }
      });

      this.mountExpress();

      // Paint whatever Liquid already knew at page load, so the first open is
      // instant rather than waiting on a fetch.
      /* Shopify's own pixel fires InitiateCheckout on the checkout page, so
       * this is the intent signal from our side of the boundary — useful for
       * spotting carts that reach the button and bounce. */
      var form = this.el.querySelector('.eu-cart__checkoutform');
      if (form) form.addEventListener('submit', function () {
        var c = self.cart || {};
        track('EUCheckoutStarted', {
          value: (c.total_price || 0) / 100, currency: 'AUD',
          num_items: c.item_count || 0
        });
      });

      var seed = document.getElementById('eu-cart-seed');
      if (seed) { try { this.render(JSON.parse(seed.textContent)); } catch (e) {} }

      /* After the seed, so the capture can see whether the cart already holds
       * the token and skip the POST. A URL with no ?offer= returns at once. */
      this.captureOffer();
    },

    /* --- express payments ------------------------------------------------ */

    /* The wallet buttons are Shopify web components that populate themselves
     * asynchronously, so the height isn't known at mount. Watch the box and
     * only offer the toggle once there is genuinely something clipped —
     * a store with one wallet enabled should never see it. */
    mountExpress: function () {
      var box = this.el.querySelector('[data-eu-express]');
      var btn = this.el.querySelector('[data-eu-express-toggle]');
      if (!box || !btn) return;
      var self = this;

      btn.addEventListener('click', function () {
        var on = box.getAttribute('data-expanded') === 'true';
        box.setAttribute('data-expanded', String(!on));
        btn.textContent = on ? 'More ways to pay' : 'Fewer payment options';
      });

      /* Fitting four wallets on one phone row.
       *
       * The element has a CLOSED shadow root, so no selector of ours reaches the
       * buttons — the only lever that crosses the boundary is Shopify's own
       * custom properties, and there isn't one for "lay out horizontally".
       * Measured, the component needs about 107px per button and stacks every
       * one full width below that. Four buttons therefore need ~446px, and a
       * phone gives ~353px, so it will always stack.
       *
       * So: give it the width it wants, then scale the result back down to the
       * width we actually have. The component lays out in one row because as
       * far as it knows there is room, and the block-size is inflated by the
       * same factor so the buttons still land at 44px on screen. Hit testing
       * follows CSS transforms, so the targets stay where they look.
       *
       * Nothing here sets --...-button-inline-size. Setting it to 100% is what
       * caused the full-width stack in the first place. */
      var PER_BUTTON = 112;   // measured floor is ~107; a little headroom
      var GAP = 6;
      var TARGET_H = 44;

      this.fitWallets = function () {
        /* Re-entrancy guard. This function sets the box's own height, which the
         * ResizeObserver below then reports as a resize, which calls it again —
         * and since the first thing it does is reset its own work in order to
         * measure honestly, the loop settles on the reset state and the fix
         * never sticks. Measured width is what matters, so track that and
         * ignore anything we caused ourselves. */
        if (self._fitting) return;
        self._fitting = true;
        try { self._fitWallets(); } finally {
          requestAnimationFrame(function () { self._fitting = false; });
        }
      };

      this._fitWallets = function () {
        var inner = box.querySelector('.dynamic-checkout__content');
        var host = box.querySelector('shopify-accelerated-checkout-cart, shopify-accelerated-checkout');
        if (!inner || !host) return;

        var kids = [].slice.call(host.children)
          .filter(function (k) { return k.getBoundingClientRect().height > 0; });
        if (!kids.length) return;

        // Reset before measuring, or we measure our own last attempt.
        inner.style.removeProperty('width');
        inner.style.removeProperty('transform');
        box.style.removeProperty('height');
        host.style.removeProperty('--shopify-accelerated-checkout-button-block-size');

        var avail = box.clientWidth;
        var need = kids.length * PER_BUTTON + (kids.length - 1) * GAP;
        if (avail <= 0 || need <= avail) return;   // it already fits; leave it alone

        var scale = avail / need;
        host.style.setProperty('--shopify-accelerated-checkout-button-block-size',
          Math.round(TARGET_H / scale) + 'px');
        inner.style.setProperty('width', need + 'px', 'important');
        inner.style.setProperty('transform', 'scale(' + scale.toFixed(4) + ')', 'important');
        inner.style.setProperty('transform-origin', '0 0', 'important');
        box.style.setProperty('height', Math.ceil(TARGET_H + 8) + 'px', 'important');
        box.style.setProperty('overflow', 'hidden', 'important');
      };

      /* Deterministic triggers, not observers.
       *
       * A ResizeObserver here fought itself: this function sets the box's own
       * height, the observer reported that as a resize, and the guard needed to
       * tell apart a real width change from one we caused. A MutationObserver
       * had the same shape of problem. The wallets only appear at a handful of
       * moments — mount, drawer open, viewport rotate — and they mount async, so
       * a few timed passes are both simpler and more reliable than watching. */
      this.refitWallets = function () {
        [0, 300, 900, 2000].forEach(function (t) { setTimeout(function () { self.fitWallets(); }, t); });
      };
      window.addEventListener('resize', function () { self.refitWallets(); });
      window.addEventListener('orientationchange', function () { self.refitWallets(); });
      this.refitWallets();

      this.syncExpress = function () {
        self.fitWallets();
        if (box.getAttribute('data-expanded') === 'true') { btn.hidden = false; return; }
        /* Everything is on one row now, so there is nothing behind the toggle.
           It only reappears if a future wallet count breaks the row. */
        btn.hidden = box.scrollHeight - box.clientHeight < 20;
      };

      if (window.ResizeObserver) {
        new ResizeObserver(function () { self.syncExpress(); }).observe(box);
      }
      this.syncExpress();
    },

    syncExpress: function () {},

    isOpen: function () { return this.el && this.el.getAttribute('data-open') === 'true'; },

    open: function () {
      if (!this.el) return;
      // Inline is permanently open and must never lock the page's scroll.
      if (this.inline) { this.syncExpress(); return; }
      this.el.setAttribute('data-open', 'true');
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.classList.add('eu-cart-is-open');
      var c = this.el.querySelector('[data-eu-cart-close]');
      if (c) c.focus();
      // Heights only resolve once the drawer is actually laid out.
      this.syncExpress();
      if (this.refitWallets) this.refitWallets();
    },

    close: function () {
      if (!this.el || this.inline) return;
      /* Closing the drawer closes any open picker with it. Otherwise a picker
       * left open behind a closed drawer keeps bundlePickOpen() true, so the
       * mid-upgrade hold on the token tin would outlive the upgrade, and an
       * abandoned upgrade would never be re-read (review, 6 Oct 2026). */
      var gm = this.el.querySelector('[data-eu-gift-modal]');
      if (gm && !gm.hidden) this.closeGiftPicker();
      this.el.setAttribute('data-open', 'false');
      document.documentElement.style.overflow = '';
      document.documentElement.classList.remove('eu-cart-is-open');
    },

    openWith: function (cart) { this.render(cart); this.open(); },

    refresh: function () {
      var self = this;
      return json('cart.js').then(function (cart) {
        self.render(cart);
        /* A /cart.js read is the one place the function's answer is settled,
         * which is why the offer check hangs here and nowhere else. With no
         * token it resolves straight through with the same cart. */
        return self.settleOfferGift(cart)
          .then(function (c) { return c || cart; })
          .then(function (c) { return self.relabelGift(c); });
      });
    },

    /* Keep the free tin's public 'Cart Reward' text true to the offer paying
     * for it (review, 6 Oct 2026). addGift writes it once, but a cart can cross
     * $219 after the email-link tin was picked (the spend reward takes over and
     * checkout labels it FREE TIN) or drop back under it (the link takes over,
     * FREE TIN FROM US). The text reaches checkout, the order and the packing
     * slip, so it is rewritten to match, the same way settleBundleLabels
     * repairs Bundle labels. Skipped while anything else might be writing to
     * the cart (a queued add, a label repair, an open bundle picker, a tin
     * about to be trimmed); the next refresh catches up. One pass, then one
     * refresh, so it cannot loop: offerGiftOnly() never reads this text. */
    relabelGift: function (cart) {
      var self = this;
      if (!cart || this._relabeling || this._settling || (this._pending || 0) > 0) return Promise.resolve(cart);
      if (this.bundlePickOpen()) return Promise.resolve(cart);
      var lines = this.giftLines(cart);
      var held = lines.reduce(function (a, l) { return a + l.quantity; }, 0);
      if (!lines.length || held > this.giftsAllowed()) return Promise.resolve(cart);

      // Capitals, matching the discount label checkout prints under the line
      // (Matthew, 6 Oct 2026). Older sentence-case text is rewritten on sight.
      var want = this.offerGiftOnly() ? this.offerTitle() : 'FREE TIN';
      var stale = lines.filter(function (l) { return ((l.properties || {})['Cart Reward'] || '') !== want; });
      if (!stale.length) return Promise.resolve(cart);

      this._relabeling = true;
      var chain = Promise.resolve();
      stale.forEach(function (l) {
        chain = chain.then(function () {
          var props = {};
          Object.keys(l.properties || {}).forEach(function (k) { props[k] = l.properties[k]; });
          props._reward = 'true';
          props['Cart Reward'] = want;
          return json('cart/change.js', {
            method: 'POST',
            body: JSON.stringify({ id: l.key, quantity: l.quantity, properties: props })
          });
        });
      });
      return chain
        .then(function () { self._relabeling = false; return self.refresh(); })
        .catch(function (e) {
          self._relabeling = false;
          console.error('[eu-cart] gift relabel', e);
          return cart;
        });
    },

    /* Cart writes run one at a time. Nothing else may POST to the cart while a
     * queued write is in flight.
     *
     * Shopify's cart is a single mutable record with no optimistic
     * concurrency. Two cart/add.js calls that overlap can both read the same
     * starting state, and the later response wins — silently dropping the
     * earlier tin. Tapping + ten times quickly is exactly that pattern, and it
     * is why a ten-tin bundle could arrive in the cart as nine while the picker
     * had honestly counted ten successful responses.
     *
     * The chain is kept permanently resolved so one failed write cannot strand
     * every write queued behind it; callers get the real promise and handle
     * their own failure. */
    enqueue: function (fn) {
      var self = this;
      self._pending = (self._pending || 0) + 1;
      var run = (self._chain || Promise.resolve()).then(fn);
      self._chain = run.then(
        function () { self._pending -= 1; },
        function (e) { self._pending -= 1; console.error('[eu-cart] queued write', e); }
      );
      return run;
    },

    /* Pull the picker's local counters back in line with the cart.
     *
     * The picker counts optimistically so taps feel instant, which means a
     * failed write leaves it ahead of reality. Rather than unwinding a specific
     * tap, re-derive the count from the cart — self-healing, and correct no
     * matter how many writes failed or in what order. */
    reconcilePicker: function () {
      var self = this;
      var m = this._pick;
      if (!m || m.kind !== 'bundle') return Promise.resolve();
      return this.refresh().then(function (cart) {
        var have = 0;
        ((cart || {}).items || []).forEach(function (l) {
          if ((l.properties || {})._bundle_id === m.bundleId) have += l.quantity;
        });
        var added = Math.max(0, have - (m.baseQty || 0));
        self._picked = (self._picked || []).slice(0, added);
        m.remaining = Math.max(0, (m.total || 0) - added);
        self.paintPicker();
      });
    },

    busy: function (on) { if (this.el) this.el.setAttribute('data-busy', String(!!on)); },

    /* --- mutations ------------------------------------------------------ */

    change: function (key, delta) {
      var line = (this.cart.items || []).filter(function (l) { return l.key === key; })[0];
      if (!line) return;
      this.setQty(key, Math.max(0, line.quantity + delta));
    },

    setQty: function (key, quantity) {
      var self = this;
      this.busy(true);
      return json('cart/change.js', { method: 'POST', body: JSON.stringify({ id: key, quantity: quantity }) })
        .then(function (cart) { self.render(cart); })
        .catch(function (err) {
          console.error('[eu-cart] change', err);
          self.notify("That didn't work — please try again.");
          return self.refresh();
        })
        .finally(function () { self.busy(false); });
    },

    // Removing a bundle removes the whole group — leaving four of five tins
    // behind would silently drop the discount and confuse the customer.
    /* Removing a bundle removes the whole group — leaving four of five tins
     * behind would silently drop the discount and confuse the customer.
     *
     * One /cart/update.js, not a chain of /cart/change.js calls. Shopify
     * RE-KEYS the surviving lines every time a line is removed, so keys read
     * before the first removal are dead by the second: the first call
     * succeeded, the rest came back 400 "no valid id or line parameter", and a
     * three-tin bundle lost exactly one tin. update.js takes every key in a
     * single atomic request, so nothing has a chance to go stale. */
    removeGroup: function (bundleId) {
      var updates = {};
      var found = false;
      (this.cart.items || []).forEach(function (l) {
        if ((l.properties || {})._bundle_id !== bundleId) return;
        updates[l.key] = 0;
        found = true;
      });
      if (!found) return;
      return this.applyUpdates(updates);
    },

    /* Shared by every multi-line mutation, for the reason above. Failures are
     * surfaced rather than logged into the void — a dead button that says
     * nothing is what made the re-keying bug invisible for so long. */
    applyUpdates: function (updates) {
      var self = this;
      this.busy(true);
      return json('cart/update.js', { method: 'POST', body: JSON.stringify({ updates: updates }) })
        .then(function (cart) { self.render(cart); return cart; })
        .catch(function (err) {
          console.error('[eu-cart] update', err);
          self.notify("That didn't work — please try again.");
          return self.refresh();
        })
        .finally(function () { self.busy(false); });
    },

    /* Visible failure. The drawer had no way to tell a customer that something
     * went wrong, so a broken action looked identical to an ignored tap. */
    notify: function (message, ms) {
      var msg = this.el.querySelector('[data-eu-cart-msg]');
      if (!msg) return;
      msg.textContent = message;
      msg.hidden = false;
      clearTimeout(this._msgTimer);
      // Four seconds unless the caller asks for longer; every existing call
      // leaves it at four.
      this._msgTimer = setTimeout(function () { msg.hidden = true; }, ms || 4000);
    },

    /* --- rewards --------------------------------------------------------- */

    /* Reward progress runs on PAID MERCHANDISE after discounts.
     *
     * Package Protection must never push someone over a threshold, a free gift
     * must never pay for the next free gift, and a subscription line earns free
     * standard postage but not free express or physical gifts. Two totals fall
     * out of that: everything paid, and everything paid that is not a
     * subscription. */
    eligible: function (cart) {
      var all = 0, oneTime = 0;
      (cart.items || []).forEach(function (l) {
        var p = l.properties || {};
        if (p._reward) return;                       // the gift itself
        if (p._protection) return;                   // package protection
        if (l.final_line_price <= 0) return;         // any $0 promo line
        if (l.gift_card) return;
        all += l.final_line_price;
        if (!l.selling_plan_allocation) oneTime += l.final_line_price;
      });
      return { all: all, oneTime: oneTime };
    },

    // What the cart has earned. Tier 1 counts subscriptions; 2-4 do not.
    entitlement: function (cart) {
      var e = this.eligible(cart);
      var reached = [], next = null, gifts = 0;
      this.tiers.forEach(function (t) {
        var basis = t.tier === 1 ? e.all : e.oneTime;
        if (basis >= t.amount) { reached.push(t); gifts = Math.max(gifts, t.gifts); }
        else if (!next) next = { tier: t, basis: basis, short: t.amount - basis };
      });
      return { reached: reached, next: next, gifts: gifts, eligible: e };
    },

    renderRewards: function (cart) {
      var wrap = this.el.querySelector('[data-eu-rewards]');
      if (!wrap) return;
      if (!this.rewardsOn || !this.tiers.length || !cart.item_count) { wrap.hidden = true; return; }
      wrap.hidden = false;

      var ent = this.entitlement(cart);
      this.gifts = ent.gifts;
      // Read by nextStep: does the upgrade clear the reward we're pointing at?
      this._nextTierAmount = ent.next ? ent.next.tier.amount : null;
      var giftTier = this.tiers.filter(function (t) { return t.gifts > 0; })[0];
      this._giftTierAmount = giftTier ? giftTier.amount : null;
      /* The figure the gift tier is actually measured against, so anything else
       * asking "how far from the free tin?" gets the same answer this bar just
       * gave. Tier 1 counts subscriptions and the tiers above it do not, so the
       * basis has to be read off the tier rather than assumed. */
      this._giftBasis = giftTier
        ? (giftTier.tier === 1 ? ent.eligible.all : ent.eligible.oneTime)
        : 0;

      /* Reward tiers crossed. Fired once each per page life: renderRewards runs
       * on every repaint, and an event per keystroke of a quantity field would
       * be useless data. */
      this._fired = this._fired || {};
      ent.reached.forEach(function (t) {
        if (this._fired[t.tier]) return;
        this._fired[t.tier] = true;
        track('EURewardUnlocked', {
          tier: t.tier, threshold: t.amount / 100,
          reward: t.label, value: ent.eligible.oneTime / 100, currency: 'AUD'
        });
      }, this);

      /* A gift token (G3/G5/G10) on a big enough bundle already brings the free
       * tin (Matthew, 6 Oct 2026), so the free-tin rung counts as reached here: the
       * bar must not ask for $29.05 more to earn a tin that is already in the
       * order. Display only; this.gifts and the amounts read by nextStep above
       * are untouched. */
      var tokenTin = (this.offerGift || 0) > 0;
      var tokenGifts = this.offerGift || 0;
      var covered = function (t) { return tokenTin && t.gifts > 0 && t.gifts <= tokenGifts; };
      var next = ent.next;
      if (next && covered(next.tier)) {
        // Skip the rung the token covers and point at the next real one, if any.
        next = null;
        for (var q = 0; q < this.tiers.length; q++) {
          var tq = this.tiers[q];
          if (ent.reached.indexOf(tq) !== -1 || covered(tq)) continue;
          var bq = tq.tier === 1 ? ent.eligible.all : ent.eligible.oneTime;
          next = { tier: tq, basis: bq, short: tq.amount - bq };
          break;
        }
      }

      var top = this.tiers[this.tiers.length - 1];
      var basis = next ? next.basis : (top.tier === 1 ? ent.eligible.all : ent.eligible.oneTime);

      /* Fill has to land ON the pips, not just somewhere along the track.
       *
       * It used to be basis/topThreshold, which is only correct if the pips are
       * spaced by value — they are spaced evenly. At $109.95 that put the fill
       * at 50% of the bar while the $179 pip sits at 50% of the track, so the
       * bar appeared to have cleared a tier it hadn't. Interpolate between pip
       * centres instead, so the fill is always exactly as far along as the
       * customer is between one reward and the next. */
      var n = this.tiers.length;
      var centre = function (i) { return (i + 0.5) / n; };
      var pct;
      if (!next) {
        pct = 100;
      } else {
        var i = this.tiers.indexOf(next.tier);
        var prevAmt = i === 0 ? 0 : this.tiers[i - 1].amount;
        var span = next.tier.amount - prevAmt;
        var within = span > 0 ? Math.max(0, Math.min(1, (basis - prevAmt) / span)) : 0;
        var from = i === 0 ? 0 : centre(i - 1);
        pct = Math.round((from + within * (centre(i) - from)) * 100);
      }

      var msg = next
        ? "You're <b>" + money(next.short) + "</b> away from " + shout(next.tier.label) + "!"
        : esc(this.completeMsg);

      /* An S3 token on a priced 3-tin bundle already has standard delivery
       * covered, so "$25.05 away from free standard delivery" would be asking
       * for money the function is not going to charge. Only the standard line
       * is replaced (rung 1 is free shipping); once the cart is chasing express
       * or the free tin the ordinary copy is right again. */
      var shipFree = !!this.offerShip;
      var shipCovered = shipFree && next && next.tier.tier === 1;
      if (shipCovered) {
        msg = '<b>Free</b> standard delivery on this order, from us!';
        // The standard pip is ticked, so fill up to it rather than short of it.
        pct = Math.max(pct, Math.round(centre(0) * 100));
      }

      var pips = this.tiers.map(function (t) {
        var done = ent.reached.indexOf(t) !== -1 || (shipFree && t.tier === 1) || covered(t);
        var isNext = next && next.tier === t && !(shipCovered && t.tier === 1);
        return '<span class="eu-rw__pip" data-done="' + done + '" data-next="' + !!isNext + '">' +
          '<i>' + (done ? '✓' : '') + '</i>' +
          '<span class="eu-rw__amt">' + money(t.amount) + '</span>' +
          '<span class="eu-rw__lbl">' + esc(t.label) + '</span>' +
          '<span class="eu-rw__lbl-s">' + esc(shortLabel(t.label)) + '</span>' +
        '</span>';
      }).join('');

      /* Once every tier is reached the bar has nothing left to motivate — the
       * track is full and each pip is a tick. Collapsing to a single confirming
       * line hands ~90px back to the item list at exactly the point in the
       * journey where seeing the order matters more than seeing the ladder. */
      wrap.setAttribute('data-complete', String(!next));

      if (!next) {
        wrap.innerHTML = '<p class="eu-rw__msg" data-complete="true">' + msg + '</p>';
        return;
      }

      wrap.innerHTML =
        '<p class="eu-rw__msg" data-complete="false">' + msg + '</p>' +
        '<div class="eu-rw__track">' +
          this.tiers.map(function (t, i) {
            // A tick at each pip centre, so the filled length is legible as
            // "past $95, part-way to $179" rather than a generic 20% bar.
            return '<span class="eu-rw__tick" style="left:' + (centre(i) * 100) + '%"></span>';
          }).join('') +
          '<span class="eu-rw__fill" style="width:' + pct + '%"></span>' +
        '</div>' +
        '<div class="eu-rw__pips" style="grid-template-columns:repeat(' + n + ',1fr)">' + pips + '</div>';
    },

    /* --- personal offers ---------------------------------------------------
     *
     * After a first order Matthew emails the customer himself, and the link in
     * that email carries ?offer=<TOKEN>. The token is parked on the cart as the
     * attribute `_eu_offer`; the bundle pricing function reads it from there
     * and decides what it is worth. The drawer's only jobs are to show the
     * right prompt and to add the reward line the function will then zero.
     *
     * The prefix names the tier and is read here for DISPLAY ONLY: S3 is free
     * standard delivery on exactly one priced 3-tin bundle, G3 / G5 / G10 one
     * free tin beside a priced bundle of 3 / 5 / 10 tins OR MORE (since 6 Oct
     * 2026, so an upgrade keeps the tin). Nothing in this file is the
     * authority on whether a token is live, redeemed or expired. The function
     * is, and settleOfferGift below is how its "no" reaches the drawer. Every
     * check fails closed: no token, an unrecognisable one, a cart that is not
     * in AUD, or no priced bundle big enough all mean nothing is promised. */
    OFFER_RE: /^(S3|G3|G5|G10)-[A-Z2-7]{10}$/,
    OFFER_TINS: { S3: 3, G3: 3, G5: 5, G10: 10 },
    OFFER_LABEL: 'Your Free Tin From Us',

    /* Give a tin, get a tin (Matthew, 26 Oct 2026). Codes only, no links.
     * A friend code is the referrer's first name + an order number
     * (JORDAN7759); a reward code is FREETIN + a number (FREETIN7760). Typed
     * into the "Got A Code From A Mate?" box below the cart, the code is kept
     * on the cart as _eu_ref and applied to checkout. It unlocks ONE free tin
     * beside a priced bundle of 3 or more, through the same picker as every
     * other free tin. The Referral codes discount is the authority: if it
     * leaves the tin at full price, settleOfferGift takes it back out. */
    REF_REWARD_RE: /^FREETIN\d{4,6}$/,
    REF_FRIEND_RE: /^[A-Z\u00C0-\u024F]{1,12}\d{4,6}$/,
    /* Paid standard tins in the cart, bundled or loose. Free tins and Package
     * Protection never count; nor does anything that isn't a single tin. */
    paidTins: function (cart) {
      var self = this;
      return ((cart || {}).items || []).reduce(function (a, l) {
        var pr = l.properties || {};
        if (pr._reward || pr._protection || !self.isTinLine(l)) return a;
        return a + l.quantity;
      }, 0);
    },

    refKind: function (code) {
      var c = String(code == null ? '' : code).trim().toUpperCase();
      if (this.REF_REWARD_RE.test(c)) return 'reward';
      if (this.REF_FRIEND_RE.test(c)) return 'friend';
      return null;
    },
    offerLabel: function () {
      if (this.offerRef === 'friend') return 'Your Free Tin From A Mate';
      if (this.offerRef === 'reward') return 'Your Free Reward Tin';
      return this.OFFER_LABEL;
    },
    offerTitle: function () {
      if (this.offerRef === 'friend') return 'FREE TIN FROM A MATE';
      if (this.offerRef === 'reward') return 'FREE TIN REFERRAL REWARD';
      return 'FREE TIN FROM US';
    },

    /* The token the cart is carrying, or null. Trimmed, never re-cased: the
     * function compares exactly, so the drawer must not be more lenient than
     * the thing that actually pays. */
    readOffer: function (cart) {
      var attrs = (cart || {}).attributes || {};
      var raw = attrs._eu_offer;
      var token = String(raw == null ? '' : raw).trim();
      if (this.OFFER_RE.test(token)) return { token: token, tier: token.split('-')[0], ref: null };
      var code = String(attrs._eu_ref == null ? '' : attrs._eu_ref).trim().toUpperCase();
      var kind = this.refKind(code);
      return kind ? { token: code, tier: 'G3', ref: kind } : null;
    },

    /* sessionStorage, as dismissed() uses it: same lifetime, same reason.
     * Wrapped because a private window can refuse it, and a refused store
     * must not take the drawer down. */
    offerStore: {
      get: function (k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
      set: function (k, v) {
        try { if (v == null) sessionStorage.removeItem(k); else sessionStorage.setItem(k, v); } catch (e) {}
      }
    },

    /* A token the function has refused once this session.
     *
     * Without this the slot would come straight back after the strip, the
     * customer would pick again, and the drawer would strip again: a loop with
     * a sad message in it. Following the email link again clears it, so a
     * token that was refused by a passing fault gets another go. */
    offerDead: function (v) {
      if (v === undefined) return this.offerStore.get('euOfferDead');
      this.offerStore.set('euOfferDead', v || null);
    },

    /* Is there a bundle of `n` tins (or, with atLeast, `n` or more) that the
     * function has priced?
     *
     * "Priced" is read off the cart rather than assumed from the count. The
     * tier discount is line-level, so a group the function accepted shows
     * original above final; a group of the right size made of something that
     * is not a tin, or one the function rejected for any reason, draws no
     * discount and so earns no offer. Same rule the function applies, read
     * from the same evidence. Off-tier groups (2, 4, 6 to 9, 11 to 14) never
     * match: the size itself is checked against TIER_PRICE, not just the
     * discount, because another line-level discount can lower an off-tier
     * group's price too. */
    hasPricedGroup: function (cart, n, atLeast) {
      var self = this;
      var groups = {};
      ((cart || {}).items || []).forEach(function (l) {
        var p = l.properties || {};
        if (p._reward || p._protection || !p._bundle_id) return;
        var g = groups[p._bundle_id] = groups[p._bundle_id] || { qty: 0, was: 0, now: 0, tins: true };
        g.qty += l.quantity;
        g.was += l.original_line_price;
        g.now += l.final_line_price;
        if (!self.isTinLine(l)) g.tins = false;
      });
      return Object.keys(groups).some(function (id) {
        var g = groups[id];
        // On-tier sizes only (3, 5, 10, 15), exactly as the function's TIERS:
        // an off-tier group discounted by something else (a product-scoped
        // code) must not pass for a priced bundle (review, 6 Oct 2026).
        var onTier = g.qty > 1 && !!self.TIER_PRICE[g.qty];
        return onTier && (atLeast ? g.qty >= n : g.qty === n) && g.tins && g.was > g.now;
      });
    },

    /* What the token in this cart is worth, for display. Runs at the top of
     * every render so the rewards bar, the gift slot and the gift sync all
     * read one answer. With no token every field is empty and nothing
     * downstream changes. */
    offerState: function (cart) {
      var o = this.readOffer(cart);
      this.offerToken = o ? o.token : null;
      this.offerTier = o ? o.tier : null;
      this.offerRef = o ? o.ref : null;
      this.offerShip = false;
      this.offerGift = 0;
      if (!o) return;

      // AUD only. The function refuses anything else, so promise nothing.
      var cur = (cart || {}).currency;
      if (cur && typeof cur === 'object') cur = cur.iso_code;
      if (cur !== 'AUD') return;
      if (this.offerDead() === o.token) return;

      // Gift tokens pay out on a priced bundle of their size OR BIGGER, so a G5
      // customer who upgrades to 10 in the drawer keeps the tin (Matthew, 6 Oct
      // 2026). The function's lines target applies the same "at least" rule.
      // S3 stays exact: it matches the function's delivery target, and a 5-tin
      // cart clears the $95 free-standard threshold on its own anyway.
      // Codes from a mate: "buy any 3 tins, get 1 free", loose tins count too
      // (Matthew, 26 Oct 2026). Email offers keep their bundle rule.
      if (o.ref) { if (this.paidTins(cart) < 3) return; }
      else if (!this.hasPricedGroup(cart, this.OFFER_TINS[o.tier], o.tier !== 'S3')) return;
      if (o.tier === 'S3') this.offerShip = true;
      else this.offerGift = 1;
    },

    /* Free tins the cart may hold, from every source. ONE at most from the
     * token, and never on top of the cart's own reward: a $219 cart with a G5
     * token still gets one tin, not two. With no token this is exactly
     * this.gifts, which is what every caller read before. */
    giftsAllowed: function () {
      return Math.max(this.gifts || 0, this.offerGift || 0);
    },

    /* The token is the only reason a free tin is on offer. Decides the copy. */
    offerGiftOnly: function () {
      return !!this.offerGift && !(this.gifts || 0);
    },

    /* Page load: ?offer=<TOKEN> on any URL goes onto the cart, once.
     *
     * One POST, then a repaint from the cart it returns. The sessionStorage
     * guard stops a reload reposting; a cart that already carries the token
     * needs nothing at all. A token that fails the format test is ignored,
     * because the function would ignore it too and parking garbage on the
     * cart helps nobody. The URL is left alone. */
    captureOffer: function () {
      var self = this;
      var token = '';
      try {
        var m = /[?&]offer=([^&#]*)/.exec(window.location.search || '');
        if (m) token = decodeURIComponent(m[1]).trim();
      } catch (e) { token = ''; }
      if (!token || !this.OFFER_RE.test(token)) return Promise.resolve(null);

      // Following the link again is a fresh go: forget any earlier refusal.
      if (this.offerDead() === token) this.offerDead(null);

      var have = this.readOffer(this.cart);
      if (have && have.token === token) return Promise.resolve(this.cart);
      if (this.offerStore.get('euOfferPosted') === token) return Promise.resolve(this.cart);

      return json('cart/update.js', {
        method: 'POST',
        body: JSON.stringify({ attributes: { _eu_offer: token } })
      })
        .then(function (cart) {
          self.offerStore.set('euOfferPosted', token);
          self.render(cart);
          track('EUOfferLanded', { tier: token.split('-')[0] });
          return cart;
        })
        .catch(function (e) { console.error('[eu-cart] offer capture', e); return null; });
    },

    /* The function's verdict, read off a settled cart.
     *
     * Only ever called from refresh(), after a /cart.js read. Never off the
     * seed and never off a mutation's own response, so the discount the
     * function grants has had its chance to appear. If a reward line is in
     * the cart because of the token (the $219 path does not cover it) and the
     * function has left it at full price, the token is not good: the line
     * goes, the customer is told in one short sentence, and the token is
     * marked dead for the session so the slot does not reappear.
     *
     * A gift the cart earned on its own is never touched here, and a cart
     * holding more gifts than it is allowed is left to syncGifts, which is
     * already trimming it. */
    settleOfferGift: function (cart) {
      if (!this.offerGift) return Promise.resolve(cart);
      var lines = this.giftLines(cart);
      var held = lines.reduce(function (a, l) { return a + l.quantity; }, 0);
      if (held <= (this.gifts || 0)) return Promise.resolve(cart);
      if (held > this.giftsAllowed()) return Promise.resolve(cart);

      var dud = lines.filter(function (l) { return l.original_line_price - l.final_line_price <= 0; });
      if (!dud.length) return Promise.resolve(cart);

      this.offerDead(this.offerToken);
      var updates = {};
      dud.forEach(function (l) { updates[l.key] = 0; });
      this.notify(this.offerRef ? "We couldn't apply that code, so your free tin was removed. Check every letter and number and try again." : "That free tin offer isn't valid any more", 6000);
      return this.applyUpdates(updates);
    },

    /* --- gifts ----------------------------------------------------------- */

    // The catalogue is rendered once in layout/theme.liquid, so it is available
    // on every page — the drawer opens everywhere, not just product pages.
    catalogue: function () {
      if (this._cat) return this._cat;
      var raw = document.getElementById('eu-bundle-data');
      try { this._cat = raw ? (JSON.parse(raw.textContent).tins || []) : []; }
      catch (e) { this._cat = []; }
      return this._cat;
    },

    /* Gifts are the customer's own pick from in-stock tins, not a dedicated
     * product. That removes the whole gift-SKU problem: these already exist,
     * are already stocked at the Hong Kong location, and Spaceship already
     * knows the SKUs. A sold-out tin disappears from the chooser for free
     * because the catalogue already tracks availability. */
    giftPool: function () {
      var allow = this.giftAllow || [];
      return this.catalogue().filter(function (t) {
        if (!t.available || t.cap < 1) return false;
        return !allow.length || allow.indexOf(t.handle) !== -1;
      });
    },

    tinByVariant: function (id) {
      if (!this._byVariant) {
        this._byVariant = {};
        this.catalogue().forEach(function (t) { this._byVariant[t.id] = t; }, this);
      }
      return this._byVariant[id] || null;
    },

    giftLines: function (cart) {
      return (cart.items || []).filter(function (l) { return (l.properties || {})._reward; });
    },

    dismissed: function (v) {
      if (v === undefined) return sessionStorage.getItem('euGiftDismissed') === '1';
      if (v) sessionStorage.setItem('euGiftDismissed', '1');
      else sessionStorage.removeItem('euGiftDismissed');
    },

    /* Entitlement can fall as well as rise — a removed item or a discount code
     * can drop the cart back under a threshold. Any gift beyond what the cart
     * currently earns is removed, newest first. */
    syncGifts: function (cart) {
      var have = this.giftLines(cart);
      var held = have.reduce(function (a, l) { return a + l.quantity; }, 0);
      // this.gifts, or the token's one tin, whichever is larger. Never both.
      var allowed = this.giftsAllowed();
      if (held <= allowed) return Promise.resolve(cart);

      /* Mid-upgrade, hold the token's tin. While a bundle picker is open the
       * group passes through off-tier sizes (6 to 9 on the way to 10) that no
       * bundle price covers, and any repaint then (minus in the picker, a label
       * repair, a failed write) would judge the token tin unearned and drop it
       * (Matthew, 6 Oct 2026). The first render after the picker closes judges
       * the finished cart instead: the abandon refresh in closeGiftPicker, or
       * the absorb / label repair that follows a completed upgrade. See
       * midUpgradeHold for exactly what is held. */
      if (this.midUpgradeHold(cart, held)) return Promise.resolve(cart);

      // A code's free tin lost because the cart fell under 3 paid tins: say so.
      if (this.offerRef && allowed < 1 && held > 0) {
        var need = Math.max(1, 3 - this.paidTins(cart));
        this.notify('Your free tin was removed because your cart has fewer than 3 paid tins. Add ' + need + ' more to choose it again.', 7000);
      }
      // Same re-keying trap as removeGroup: batched into one atomic update.
      var excess = held - allowed;
      var updates = {};
      have.slice().reverse().forEach(function (l) {
        if (excess <= 0) return;
        var drop = Math.min(excess, l.quantity);
        excess -= drop;
        updates[l.key] = l.quantity - drop;
      });
      return this.applyUpdates(updates);
    },

    addGift: function (variantId) {
      var self = this;
      this.busy(true);
      this.dismissed(false);
      /* Same `_reward` line either way; that is all the function reads. The
       * public label is the only difference, so the packing slip says where
       * the tin came from. */
      var fromOffer = this.offerGiftOnly();
      return json('cart/add.js', {
        method: 'POST',
        body: JSON.stringify({ items: [{
          id: Number(variantId), quantity: 1,
          properties: { _reward: 'true', 'Cart Reward': fromOffer ? this.offerTitle() : 'FREE TIN' }
        }] })
      })
        .then(function () {
          var t = self.tinByVariant(Number(variantId));
          var ev = {
            content_ids: [String(variantId)],
            content_name: t ? t.brand + ' ' + t.flavour : String(variantId),
            value: 0, currency: 'AUD'
          };
          if (fromOffer) ev.offer = self.offerTier;
          track('EUGiftChosen', ev);
          /* refresh() runs settleOfferGift: if the function leaves this line at
           * full price, it comes straight back out with a message. */
          return self.refresh();
        })
        .then(function () { self.closeGiftPicker(); })
        .catch(function (e) { console.error('[eu-cart] gift add', e); })
        .finally(function () { self.busy(false); });
    },

    renderGiftSlot: function (cart) {
      var slot = this.el.querySelector('[data-eu-gift]');
      if (!slot) return;

      // The cart's own reward, or the token's one tin. Never the two added up.
      var allowed = this.giftsAllowed();
      var held = this.giftLines(cart).reduce(function (a, l) { return a + l.quantity; }, 0);
      var open = allowed - held;

      if (allowed < 1 && this.offerRef && this.offerDead() !== this.offerToken) {
        // A code is waiting but there's no priced bundle of 3+ yet: say what unlocks it.
        slot.hidden = false;
        slot.innerHTML = '<div class="eu-gift__prompt">' +
          '<span class="eu-gift__badge">Free</span>' +
          '<span class="eu-gift__text"><b>Your Free Tin Is Waiting!</b>' +
            '<span>' + (function (n) { return n > 0 ? 'Add ' + n + ' more tin' + (n > 1 ? 's' : '') + ', then choose your free flavour.' : 'Buy 3 or more tins, then choose your free flavour.'; })(3 - this.paidTins(cart)) + '</span></span>' +
        '</div>';
        return;
      }
      if (allowed < 1 || open < 1) { slot.hidden = true; slot.innerHTML = ''; return; }

      var pool = this.giftPool();
      if (!pool.length) {
        // §4: never promise a gift we cannot actually give.
        slot.hidden = true; slot.innerHTML = '';
        console.warn('[eu-cart] gift tier unlocked but no in-stock gift products available');
        return;
      }

      /* The token's wording only when the token is the sole reason the slot
       * is open. A cart that has earned its own tin keeps the ordinary copy,
       * because that is the tin it is getting. Same picker, same allow-list. */
      var fromOffer = this.offerGiftOnly();

      slot.hidden = false;
      slot.innerHTML = this.dismissed()
        ? '<button type="button" class="eu-gift__quiet" data-eu-gift-open>' +
            (fromOffer
              ? esc(this.offerLabel()) + ' Is Still To Claim!'
              : 'You still have ' + open + ' free tin' + (open > 1 ? 's' : '') + ' to claim!') +
          '</button>'
        : '<div class="eu-gift__prompt">' +
            '<span class="eu-gift__badge">Free</span>' +
            (fromOffer
              ? '<span class="eu-gift__text"><b>' + (this.offerRef ? 'Choose Your Free Tin!' : esc(this.offerLabel()) + '!') + '</b>' +
                  '<span>' + (this.offerRef === 'reward' ? 'Pick 1 extra tin free. Check out with the email your reward was sent to.' : this.offerRef ? 'Pick 1 extra tin free.' : 'Pick any flavour') + '</span></span>'
              : '<span class="eu-gift__text"><b>Choose Your FREE Tin' + (open > 1 ? 's' : '') + '!</b>' +
                  '<span>' + open + ' Available</span></span>') +
            '<button type="button" class="eu-gift__cta" data-eu-gift-open>' + (fromOffer && this.offerRef ? 'Choose Flavour' : 'Choose') + '</button>' +
          '</div>';
    },

    openGiftPicker: function () {
      // Heading follows the slot; everything else about the picker is shared.
      // Code tins open with every flavour already showing: one tap to pick.
      this.openPicker(this.offerGiftOnly() ? { kind: 'gift', head: this.offerRef ? 'Choose Your Free Tin' : this.offerLabel() + '!', openAll: !!this.offerRef } : { kind: 'gift' });
    },

    /* One modal, three jobs: claim a free tin, add tins into an existing
     * bundle, or add a loose tin. The pool and the markup are identical — only
     * the heading and what happens on pick differ. */
    /* The same picker the product page uses.
     *
     * This was a plainer list — image, flavour, pouches, a Pick button. The
     * customer has already learned the richer one choosing their bundle, so
     * meeting a different, thinner picker at the upgrade is a step backwards
     * exactly where confidence matters most. Same search, same brand
     * accordions, same cards, same stepper, same progress. */
    openPicker: function (mode) {
      this._pick = mode || { kind: 'gift' };
      this._pick.total = this._pick.kind === 'gift' ? 1 : (this._pick.remaining || 1);
      this._pq = '';
      this._pbrand = this._pick.openAll ? 'ALL' : null;
      this._picked = [];

      /* How many tins the group already held when the picker opened. Each add's
       * position in the group is then baseQty + how many we have picked, worked
       * out locally — which is what lets a tap skip the cart round-trip that
       * used to sit between every pair of taps. */
      if (this._pick.kind === 'bundle') {
        var bid = this._pick.bundleId;
        var base = 0;
        ((this.cart || {}).items || []).forEach(function (l) {
          if ((l.properties || {})._bundle_id === bid) base += l.quantity;
        });
        this._pick.baseQty = base;
      }

      this.paintPicker();
    },

    /** Everything the picker can offer, given why it is open. */
    pickerPool: function () {
      var self = this;
      var all = this._pick.kind === 'gift'
        ? this.giftPool()
        : this.catalogue();
      // Sold out stays visible but sinks, exactly as on the product page.
      return all.slice().sort(function (a, b) {
        return (a.cap > 0 ? 0 : 1) - (b.cap > 0 ? 0 : 1);
      });
    },

    /** How many of this flavour the customer has already chosen this session. */
    pickerUsage: function (id) {
      return (this._picked || []).filter(function (x) { return String(x) === String(id); }).length;
    },

    pickerCard: function (t) {
      var used = this.pickerUsage(t.id);
      var out = t.cap < 1;
      var maxed = used >= t.cap;
      var s = strengthBand(t.caffeine);

      var dots = '';
      if (s) for (var i = 1; i <= 4; i++) dots += '<i class="eu-dot' + (i <= s.level ? ' is-on' : '') + '"></i>';

      var meta = [];
      if (s) meta.push('<span class="eu-str">' + dots + '</span>' + esc(s.label) +
                       (t.caffeine ? ' · ' + t.caffeine + 'mg caffeine' : ''));
      meta.push(t.pouches + ' Pouches');

      return '<div class="eu-row" data-eu-gift-pick="' + t.id + '"' +
          (out ? ' data-disabled="true"' : ' role="button" tabindex="0"') +
          ' aria-label="' + esc(t.brand + ' ' + t.flavour) + '">' +
        (t.image ? '<img class="eu-row__img" src="' + esc(t.image) + '" alt="" loading="lazy" width="56" height="56">'
                 : '<span class="eu-row__img"></span>') +
        '<span class="eu-row__body">' +
          '<span class="eu-row__top">' +
            '<span class="eu-row__name">' + esc(t.flavour) + '</span>' +
            (t.rating ? '<span class="eu-row__rating">★ ' + Number(t.rating).toFixed(1) +
                        (t.reviews ? ' <i>(' + t.reviews + ')</i>' : '') + '</span>' : '') +
          '</span>' +
          (t.notes ? '<span class="eu-row__notes">' + esc(t.notes) + '</span>' : '') +
          '<span class="eu-row__meta">' + meta.map(function (m) { return '<span>' + m + '</span>'; }).join('') + '</span>' +
          (t.nootropics ? '<span class="eu-row__noo" title="' + esc(t.actives || '') + '">+ Nootropic Blend</span>' : '') +
        '</span>' +
        '<span class="eu-row__action">' +
          (out ? '<span class="eu-row__state">Sold Out</span>'
               : used > 0
                 ? '<span class="eu-row__qty">' +
                     '<button type="button" data-eu-pick-dec="' + t.id + '" aria-label="Remove one">&minus;</button>' +
                     '<b>' + used + '</b>' +
                     '<button type="button" data-eu-pick-inc="' + t.id + '"' + (maxed ? ' disabled' : '') +
                       ' aria-label="Add another">+</button>' +
                   '</span>'
                 : '<span class="eu-row__plus" aria-hidden="true">+</span>') +
        '</span>' +
      '</div>';
    },

    paintPicker: function () {
      var self = this;
      /* Repainting replaces innerHTML, which resets scrollTop — so picking a
       * flavour from halfway down a 62-item list threw the customer back to the
       * top. Carry the position across the repaint. */
      var prev = this.el.querySelector('[data-eu-gift-modal] .eu-modal__body');
      var keepScroll = prev ? prev.scrollTop : 0;
      var m = this.el.querySelector('[data-eu-gift-modal]');
      var mode = this._pick;
      var pool = this.pickerPool();

      var need = mode.total || 1;
      var got = (this._picked || []).length;

      var heads = {
        gift:   mode.head || 'Choose Your FREE Tin!',
        bundle: 'Add ' + mode.remaining + ' More Tin' + (mode.remaining > 1 ? 's' : ''),
        loose:  'Choose Another Flavour'
      };

      var segs = '';
      for (var i = 0; i < need; i++) segs += '<i class="eu-prog__seg' + (i < got ? ' is-on' : '') + '"></i>';

      var body;
      if (this._pq) {
        var q = this._pq.toLowerCase();
        var hits = pool.filter(function (t) {
          return (t.flavour + ' ' + t.brand + ' ' + (t.notes || '')).toLowerCase().indexOf(q) !== -1;
        });
        body = hits.length
          ? '<div class="eu-fgrid">' + hits.map(function (t) { return self.pickerCard(t); }).join('') + '</div>'
          : '<p class="eu-modal__none"><b>No flavours found</b>Try another flavour or brand.</p>';
      } else {
        var brands = [];
        pool.forEach(function (t) { if (brands.indexOf(t.brand) === -1) brands.push(t.brand); });
        brands.sort();
        var live = function (list) { return list.filter(function (t) { return t.cap > 0; }).length; };
        var sections = [{ key: 'ALL', label: 'All Brands', items: pool }].concat(
          brands.map(function (b) {
            var items = pool.filter(function (t) { return t.brand === b; });
            return { key: b, label: b, items: items, live: live(items) };
          }).sort(function (a, b) { return (b.live > 0) - (a.live > 0); })
        );
        body = '<div class="eu-acc">' + sections.map(function (sec) {
          var open = self._pbrand === sec.key;
          return '<div>' +
            '<button type="button" class="eu-acc__row" aria-expanded="' + open + '" data-eu-pick-acc="' + esc(sec.key) + '">' +
              '<span><svg class="eu-acc__chev" viewBox="0 0 24 24" width="11" height="11" aria-hidden="true">' +
                '<path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="3" ' +
                  'stroke-linecap="round" stroke-linejoin="round"/></svg> ' + esc(sec.label) + '</span>' +
              '<span class="eu-acc__count">' +
                (sec.key === 'ALL' ? sec.items.length
                  : (sec.live > 0 ? sec.live + ' available' : 'Sold out')) + '</span>' +
            '</button>' +
            '<div class="eu-acc__panel"' + (open ? '' : ' hidden') + '>' +
              (open ? '<div class="eu-fgrid">' + sec.items.map(function (t) { return self.pickerCard(t); }).join('') + '</div>' : '') +
            '</div>' +
          '</div>';
        }).join('') + '</div>';
      }

      var left = need - got;
      m.innerHTML =
        '<div class="eu-modal__backdrop" data-eu-gift-close></div>' +
        '<div class="eu-modal__panel" role="dialog" aria-modal="true" aria-label="' + esc(heads[mode.kind]) + '">' +
          '<div class="eu-modal__head">' +
            '<span class="eu-modal__title">' + esc(heads[mode.kind]) +
              '<span class="eu-prog" data-complete="' + (left <= 0) + '">' +
                '<span class="eu-prog__bar">' + segs + '</span>' +
                '<span class="eu-prog__label">' +
                  (left <= 0 ? 'All Chosen' : got + ' of ' + need + ' Chosen') + '</span>' +
              '</span>' +
            '</span>' +
            '<button type="button" class="eu-modal__close" data-eu-gift-close aria-label="Close">&times;</button>' +
          '</div>' +
          '<div class="eu-modal__search">' +
            '<input type="search" class="eu-search" data-eu-pick-search placeholder="Search flavours"' +
              ' value="' + esc(this._pq) + '" autocomplete="off" autocapitalize="none"' +
              ' spellcheck="false" aria-label="Search flavours">' +
          '</div>' +
          '<div class="eu-modal__body">' + body + '</div>' +
          '<div class="eu-modal__foot"' + (left <= 0 ? ' data-complete="true"' : '') + '>' +
            /* Settling: the last tin is chosen and the queued cart writes are
             * still draining. Say so. Without this the final tap looked like
             * it had done nothing at all while several writes finished in
             * order — the one pause a customer actually notices, because it is
             * the only tap that does not change anything on screen.
             *
             * NOT `_settling`. That name is taken by settleBundleLabels'
             * re-entrancy guard, and borrowing it would both suppress label
             * repair and let two repair chains run at once — the exact fault
             * its comment describes, which left groups mislabelled at
             * checkout. */
            (self._pickSettling
              ? '<button type="button" class="eu-modal__done" disabled>Adding to cart…</button>'
              : left <= 0
                ? '<button type="button" class="eu-modal__done" data-eu-pick-done>Done</button>'
                : '<span>' + got + ' of ' + need + ' Selected · Choose ' + left + ' More</span>') +
          '</div>' +
        '</div>';
      m.hidden = false;

      /* Apply the position captured above.
       *
       * Set it twice on purpose. The first assignment lands the moment the new
       * markup has layout, which covers the common case. But the flavour cards
       * carry unsized <img>s, and each one that decodes afterwards grows
       * scrollHeight — if the browser had clamped our scrollTop to a shorter
       * document, the position drifts back toward the top. The rAF pass
       * re-asserts it once that settles.
       *
       * Guarded by a scroll-position check rather than applied blindly, so a
       * customer who flicks the list during that frame keeps their own scroll
       * instead of being yanked back. */
      if (keepScroll > 0) {
        var next = m.querySelector('.eu-modal__body');
        if (next) {
          next.scrollTop = keepScroll;
          var settled = next.scrollTop;
          requestAnimationFrame(function () {
            var el = m.querySelector('.eu-modal__body');
            if (el && el.scrollTop === settled && settled !== keepScroll) el.scrollTop = keepScroll;
          });
        }
      }
    },


    closeGiftPicker: function () {
      var m = this.el.querySelector('[data-eu-gift-modal]');
      if (m) { m.hidden = true; m.innerHTML = ''; }

      /* An upgrade abandoned part-way (picked 2 of 5, then closed) leaves the
       * drawer showing the cart from before the picks, and syncGifts held the
       * token tin while the picker was open. Re-read the cart once the queued
       * adds have landed, so the drawer shows the real total and judges the
       * free tin on the bundle the customer actually ended with. _picked is
       * cleared first so this can never fire twice (render's empty-cart branch
       * also calls closeGiftPicker). */
      var p = this._pick, self = this;
      if (p && p.kind === 'bundle' && p.remaining > 0 && (this._picked || []).length) {
        this._picked = [];
        this.enqueue(function () { return self.refresh(); });
      }
    },

    /** A bundle picker (upgrade or completion) is on screen right now. */
    bundlePickOpen: function () {
      var modal = this.el && this.el.querySelector('[data-eu-gift-modal]');
      return !!(this._pick && this._pick.kind === 'bundle' && modal && !modal.hidden);
    },

    /* Hold the gift token's single tin through an upgrade in progress, and
     * nothing else: a G-tier token, no spend reward in play, at most one tin
     * held, a bundle picker open with at least one tin already picked, and the
     * group no smaller than when the picker opened. A group shrunk elsewhere,
     * a free-shipping (S3) token, or a picker opened but not yet used is judged
     * normally. */
    midUpgradeHold: function (cart, held) {
      var tier = this.offerTier;
      if (!tier || tier === 'S3' || (this.gifts || 0) || held > 1) return false;
      if (!this.bundlePickOpen() || !(this._picked || []).length) return false;
      var bid = this._pick.bundleId, qty = 0;
      ((cart || {}).items || []).forEach(function (l) {
        if ((l.properties || {})._bundle_id === bid) qty += l.quantity;
      });
      return qty >= (this._pick.baseQty || 0);
    },

    /* --- package protection ----------------------------------------------- */

    protectionLine: function (cart) {
      return (cart.items || []).filter(function (l) { return (l.properties || {})._protection; })[0] || null;
    },

    /* Opt-in, not opt-out. Pre-ticking a paid add-on the customer did not ask
     * for is the kind of thing that generates chargebacks, and the brief is
     * explicit that this only ships if there is a real policy behind it. */
    renderProtection: function (cart) {
      var row = this.el.querySelector('[data-eu-protection]');
      if (!row) return;

      var usable = this.protectionOn && this.protectionId && (cart.items || []).length;
      if (!usable) { row.hidden = true; return; }

      row.hidden = false;
      row.querySelector('[data-eu-protection-blurb]').textContent = this.protectionBlurb;
      row.querySelector('[data-eu-protection-price]').textContent = money(this.protectionPrice);

      var line = this.protectionLine(cart);
      var box = row.querySelector('[data-eu-protection-toggle]');
      // Only touch it when it disagrees, so a repaint mid-click doesn't fight
      // the customer for the checkbox.
      if (box.checked !== !!line) box.checked = !!line;
    },

    toggleProtection: function (on) {
      var self = this;
      var cart = this.cart || { items: [] };
      var line = this.protectionLine(cart);

      if (!on) {
        if (!line) return Promise.resolve();
        return this.setQty(line.key, 0);
      }
      if (line) return Promise.resolve();

      this.busy(true);
      return json('cart/add.js', {
        method: 'POST',
        body: JSON.stringify({ items: [{
          id: Number(this.protectionId), quantity: 1,
          properties: { _protection: 'true' }
        }] })
      })
        .then(function () { track('EUProtectionAdded', { value: self.protectionPrice / 100, currency: 'AUD' }); })
        .then(function () { return self.refresh(); })
        .catch(function (e) {
          console.error('[eu-cart] protection', e);
          var box = self.el.querySelector('[data-eu-protection-toggle]');
          if (box) box.checked = false;
        })
        .finally(function () { self.busy(false); });
    },

    /* --- the next step ------------------------------------------------------ */

    /* Quantity breaks are 1 / 3 / 5 / 10 at $29.95 / $69.95 / $109.95 / $189.95,
     * so every upgrade has a concrete price and a concrete reason. Generic
     * "you're $25.05 away from free shipping" wastes that — it describes a
     * threshold instead of offering the deal that clears it. */
    /* Tier prices in cents, so the per-tin rate is derived rather than written
     * down twice and allowed to drift. */
    TIER_PRICE: { 1: 2995, 3: 6995, 5: 10995, 10: 18995, 15: 26995 },

    LADDER: [
      { from: 1,  to: 3,  head: 'MAKE IT 3',  perk: '' },
      { from: 3,  to: 5,  head: 'MAKE IT 5',  perk: 'FREE Standard Delivery' },
      { from: 5,  to: 10, head: 'MAKE IT 10', perk: 'FREE Express Delivery' },
      /* Cart-exclusive. Absent from the product page's quantity breaks by
       * design, so the only way to reach it is by upgrading a 10-pack here. At
       * $269.95 it also clears the $219 gift threshold, which is why its badge
       * leads with the free tin rather than the per-tin rate. */
      { from: 10, to: 15, head: 'MAKE IT 15', perk: 'Choose a FREE Tin', exclusive: true }
    ],

    /* The biggest bundle in the cart, plus anything ungrouped. */
    /* Is this cart line one of OUR tins?
     *
     * The ladder only means anything for individual tins. A premade pack —
     * BERSERKER Sweet & Smooth, R4VE Ultimate and the rest — is a componentised
     * Shopify Bundle with its own fixed price, and it carries no _bundle_id, no
     * _reward and no _protection. Every "is this loose?" test in here used to be
     * written as "none of those three", so a pack fell straight through and was
     * counted as one loose tin at its full pack price.
     *
     * The damage was not cosmetic. A 4-tin group plus a Sweet & Smooth pack read
     * as five tins costing $209.75 — $41.95 a tin — and the drawer offered to
     * "regroup and save $99.80" by repricing them as a 5-pack. Accepting would
     * have stamped a _bundle_id onto a componentised bundle line, which is not a
     * tin, cannot be priced by the tier function, and would have taken the pack
     * apart in the process.
     *
     * The catalogue is the authority: eu-bundle-data is filtered on
     * custom.bundle_eligible, which excludes every pack by definition. If a line
     * is not in it, it is not a tin and the ladder leaves it alone. Anything
     * else — a future accessory, a merch SKU — is handled for free. */
    isTinLine: function (line) {
      return !!this.tinByVariant(line && line.variant_id);
    },

    bundleState: function (cart) {
      var groups = {}, loose = 0, self = this;
      (cart.items || []).forEach(function (l) {
        var pr = l.properties || {};
        if (pr._reward || pr._protection) return;
        if (pr._bundle_id) groups[pr._bundle_id] = (groups[pr._bundle_id] || 0) + l.quantity;
        else if (self.isTinLine(l)) loose += l.quantity;
      });
      var best = null;
      Object.keys(groups).forEach(function (id) {
        if (!best || groups[id] > best.qty) best = { id: id, qty: groups[id] };
      });
      return { group: best, loose: loose };
    },

    /* Tiers, ascending. Written once so nothing has to know the list by heart. */
    tiersAsc: function () {
      return Object.keys(this.TIER_PRICE).map(Number).sort(function (a, b) { return a - b; });
    },

    /* "You have four tins. Five cost less per tin. Shall I fix that?"
     *
     * Only fires when there is something loose to absorb — a group on its own
     * is already handled by the ladder and the off-tier branch. Returns null
     * when consolidating would cost more per tin than the customer pays now,
     * which happens if the loose tins alone already outnumber the next tier. */
    /* One id for the bundle a loose-only cart is about to become, held steady
     * across renders so the picker and the absorb that follows it cannot end up
     * stamping two different ids and splitting the group in half. Cleared once
     * the absorb lands. */
    looseGroupId: function () {
      if (!this._looseGroupId) this._looseGroupId = uuid();
      return this._looseGroupId;
    },

    completionStep: function (cart, st) {
      if (!st.loose) return null;

      /* A cart with loose tins and NO group is the commonest way to reach this
       * function, not an edge case: every quick add from a collection page
       * makes one. Bailing on it meant three quick-added tins billed at $89.85
       * while the same three as a bundle are $69.95, and nothing on screen said
       * so. Treat "no group" as a group of zero and let the arithmetic below
       * decide — it already refuses any offer that is not cheaper per tin. */
      var groupQty = st.group ? st.group.qty : 0;
      var groupId = st.group ? st.group.id : this.looseGroupId();

      var total = groupQty + st.loose;
      var tier = this.tiersAsc().filter(function (n) { return n >= total; })[0];
      if (!tier) return null;

      var payingNow = (st.group ? this.groupNet(cart, groupId) : 0) + this.looseNet(cart);
      var thenPrice = this.TIER_PRICE[tier];
      var addTins = tier - total;

      /* Never offer a consolidation that costs more per tin than the cart is
       * charging today. Without this a 15-tin group plus a loose tin would be
       * offered a "completion" that is simply a worse deal. */
      if (thenPrice / tier >= payingNow / total) return null;

      var cost = thenPrice - payingNow;

      /* A better rate that still costs more money is an upgrade, not a
       * correction, and it defers to the gift gate exactly as the ladder does.
       * Eleven tins at $219.90 would otherwise be told it is "paying more than
       * it needs to" and offered a fifteen-pack for $50.05 MORE — a fine offer,
       * but not that sentence, and not to someone who has already unlocked
       * everything there is to unlock. */
      if (cost > 0 && (this.gifts || 0) > 0) return null;

      return {
        kind: 'complete',
        bundleId: groupId,
        remaining: addTins,
        fromTins: total, toTins: tier,
        rateNow: Math.round(payingNow / total),
        rateNext: Math.round(thenPrice / tier),
        cost: cost,
        /* True only when the whole cart gets cheaper. Everything else is
         * "finish the bundle", which is honest about costing money. */
        undercuts: cost <= 0,
        payingNow: payingNow
      };
    },

    nextStep: function (cart) {
      var maxedOut = (this.gifts || 0) > 0;

      var st = this.bundleState(cart);

      /* Runs BEFORE the group guard. A completion can be the entire offer with
       * no group at all — loose tins on their own are exactly that case — and
       * everything below this point genuinely does need a group. */
      var completionFirst = this.completionStep(cart, st);
      if (completionFirst) return completionFirst;

      if (!st.group) return null;

      /* A bundle with loose tins sitting beside it is the one state the ladder
       * cannot read. Three in a group plus one added from the flavour strip is
       * $99.90 for four tins — $24.98 each, when five cost $109.95 at $21.99 —
       * and the rung at from:3 would cheerfully offer "2 more tins for $40",
       * taking them to six and never mentioning the fifth tin sitting $10.05
       * away. Count what is actually in the cart, and offer to finish it.
       *
       * This runs ahead of the gift gate. It is a correction, not a pitch: the
       * customer is already paying more per tin than the cart's own pricing
       * says they should, and that is true whether or not they have a free tin
       * coming. */

      /* A 10-pack is already a tier. Adding an 11th INTO the group would take
       * it off-tier and lose the whole $109.55 discount, so this one rung adds
       * a loose tin instead: $189.95 + $29.95 = $219.90, which clears the gift
       * threshold exactly. */
      var rung = this.LADDER.filter(function (r) { return r.from === st.group.qty; })[0];

      /* Stranded between tiers — an upgrade started and abandoned. 8 tins bill
       * at 8 x $29.95 = $239.60, which is $49.65 MORE than the 10-pack. The
       * customer is paying more for fewer tins and nothing on screen says so,
       * because there is no ladder rung at 8. */
      if (!rung) {
        var q = st.group.qty;
        var nextTier = Object.keys(this.TIER_PRICE)
          .map(Number).sort(function (a, b) { return a - b; })
          .filter(function (n) { return n > q; })[0];
        if (!nextTier) return null;

        var list = q * 2995;
        var payingNow = this.groupNet(cart, st.group.id) || list;
        var thenPrice = this.TIER_PRICE[nextTier];
        var addTins = nextTier - q;

        /* The gift gate stops us pitching upgrades to someone who has already
         * unlocked everything — but this branch is not always a pitch. At 12
         * tins the customer pays $287.52 while the 15-tin tier is $269.95:
         * three more tins for $17.57 LESS. Staying quiet there is not restraint,
         * it is letting someone overpay because we had already sold them the
         * reward. Corrections always show; genuine upsells still defer. */
        if (maxedOut && payingNow <= thenPrice) return null;

        return {
          kind: 'bundle', bundleId: st.group.id, remaining: addTins,
          offTier: true,
          /* Whether they are *actually* on full price. A store-wide automatic
           * discount can be reducing an off-tier group underneath us, and
           * telling someone already getting 20% off that they are paying full
           * price is the kind of thing they check and then stop believing the
           * rest of the card. */
          atFullPrice: payingNow >= list,
          head: 'Finish your ' + nextTier + '-Tin bundle',
          fromTins: q, toTins: nextTier,
          rateNow: Math.round(payingNow / q), rateNext: Math.round(thenPrice / nextTier),
          cost: thenPrice - payingNow,
          saveVsNow: payingNow - thenPrice,
          perk: '', crossesReward: false, crossesGift: false, exclusive: false,
          cta: 'Choose Flavours', note: 'Pick ' + addTins + ' more'
        };
      }

      /* A ladder rung is an upsell by definition — the customer is on a tier and
       * being asked up to the next one. Once every reward is unlocked there is
       * nothing left to chase, and the brief is explicit that we stop
       * manufacturing incentives at that point. */
      if (maxedOut) return null;

      /* The persuasive number is the rate they move to, not the cash they add.
       * "Add 5 tins for $80" describes a cost; "$21.99 -> $19.00 a tin" describes
       * the reason. Both are shown, with the rate leading. */
      var now = this.TIER_PRICE[rung.from], next = this.TIER_PRICE[rung.to];
      var add = rung.to - rung.from;
      var rateNow = Math.round(now / rung.from), rateNext = Math.round(next / rung.to);
      /* A gift token already brings the free tin and survives the upgrade, so
       * the card must not sell a second one (one free tin per cart, ever). The
       * upgrade itself still shows: the per-tin price still drops. */
      var tokenTin = (this.offerGift || 0) > 0;
      var tinPerk = tokenTin && /free tin/i.test(rung.perk || '') && !/second/i.test(rung.perk || '');
      // Same for an S3 link holder: standard delivery is already free on this
      // order, so the upgrade must not sell it as the perk (review, 6 Oct 2026).
      var shipPerk = !!this.offerShip && /standard/i.test(rung.perk || '');
      return {
        kind: 'bundle', bundleId: st.group.id, remaining: add,
        head: 'Upgrade to ' + rung.to + ' tins',
        fromTins: rung.from, toTins: rung.to,
        fromTotal: now, toTotal: next,
        rateNow: rateNow, rateNext: rateNext,
        savePerTin: rateNow - rateNext,
        cost: next - now, perk: (tinPerk || shipPerk) ? '' : rung.perk,
        // Express is still a real reward to cross; only the free-tin rung is moot.
        crossesReward: !shipPerk && !(tokenTin && this._nextTierAmount === this._giftTierAmount)
                       && !!(this._nextTierAmount && now < this._nextTierAmount
                          && next >= this._nextTierAmount),
        crossesGift: !tokenTin && !!(this._giftTierAmount && now < this._giftTierAmount
                        && next >= this._giftTierAmount),
        exclusive: !!rung.exclusive,
        cta: 'Upgrade Now', note: 'Pick ' + add + ' more'
      };
    },

    /* Returns markup, and renders BELOW the cart items.
     *
     * Above them it took ~440px and left the customer's actual order as a
     * sliver, so the drawer answered "would you like to buy more?" before
     * "what am I buying?". Order is now rewards, your cart, the upgrade, other
     * flavours, checkout. Flattened from a two-column card to four short rows,
     * which costs about a third of the height and reads faster. */
    nextStepHtml: function (cart) {
      var self = this;
      var step = this._step = this.nextStep(cart);
      if (!step || !(cart.items || []).length) return '';

      /* The completion card. Deliberately the same shape as the upgrade card —
       * eyebrow, headline, rate chips, CTA — because it is the same decision
       * from the customer's side. What changes is that the headline leads with
       * the per-tin price they are on, since that is the thing that is wrong. */
      if (step.kind === 'complete') {
        var free = step.undercuts;
        var short = step.remaining;

        var cHead = free
          ? 'Regroup and save ' + money(Math.abs(step.cost)) + '.'
          : short + (short === 1 ? ' more tin' : ' more tins') +
            ' and pay ' + money(step.rateNext) + ' a tin.';

        var cSupport = free
          ? 'Your ' + step.fromTins + ' tins price as a ' + step.toTins + '-pack'
          : 'You’re on ' + money(step.rateNow) + ' a tin — the ' +
            step.toTins + '-pack is ' + money(step.rateNext);

        return '<div class="eu-up" data-warn="true">' +
            '<span class="eu-up__eyebrow">' +
              (free ? 'You’re Paying More Than You Need To' : 'Finish Your Bundle') +
            '</span>' +
            '<b class="eu-up__headline">' + esc(cHead) + '</b>' +
            '<span class="eu-up__support">' + esc(cSupport) + '</span>' +

            '<div class="eu-up__chips">' +
              '<span class="eu-up__chip">' +
                '<b>' + step.fromTins + (step.fromTins === 1 ? ' Tin' : ' Tins') + '</b>' +
                '<span>' + money(step.rateNow) + '/tin</span>' +
              '</span>' +
              '<span class="eu-up__arrow" aria-hidden="true">&rarr;</span>' +
              '<span class="eu-up__chip eu-up__chip--to">' +
                '<b>' + step.toTins + ' Tins</b>' +
                '<span>' + money(step.rateNext) + '/tin</span>' +
              '</span>' +
            '</div>' +

            '<button type="button" class="eu-up__cta" data-eu-next>' +
              (free
                ? 'Regroup My ' + step.toTins + ' Tins'
                : 'Choose ' + short + ' More Flavour' + (short > 1 ? 's' : '')) +
            '</button>' +

            '<span class="eu-up__note">' +
              (free
                ? 'Nothing changes but the price.'
                : 'Your existing ' + step.fromTins + ' flavours stay selected.') +
            '</span>' +
          '</div>';
      }

      if (step.kind === 'bundle') {
        var extra = step.toTins - step.fromTins;
        var each = Math.round(step.cost / extra);

        /* One story, in order: what you get, why it is worth it, what it
         * unlocks, what to do. The old card ran six competing messages and put
         * the CTA above half of them, so the customer was asked to act before
         * the card had finished explaining why. */
        /* An off-tier group is only worth shouting about when finishing it
         * actually costs less than the customer is being charged today. Once a
         * store-wide discount is in play that stops being automatic: 7 tins at
         * 20% off is $167.72, and the 10-tin tier at $189.95 is more money for
         * more tins \u2014 a good offer, but a different sentence. Claiming a saving
         * there would be untrue, so the card falls back to the same honest
         * framing the on-tier rungs use. */
        var undercuts = step.offTier && step.saveVsNow > 0;
        /* Recorded on the step so the upsell strip below can see it without
         * working it out a second time. */
        step.undercuts = undercuts;

        var eyebrow = undercuts ? 'You\u2019re Paying More Than You Need To'
                    : step.offTier ? 'Finish Your Bundle'
                    : step.exclusive ? 'Cart Exclusive'
                    : 'Upgrade Your Bundle';

        var tinWord = extra > 1 ? ' More Tins' : ' More Tin';
        var headline = undercuts
          ? extra + tinWord + '. Save ' + money(step.saveVsNow) + '.'
          : extra + tinWord + '. Just ' + money(Math.abs(step.cost), true) + '.';

        var support = undercuts
          ? 'Finish the ' + step.toTins + '-tin bundle and pay less than you are now'
          : step.offTier
            ? 'Reach the ' + step.toTins + '-tin price of ' +
              money(Math.round(self.TIER_PRICE[step.toTins] / step.toTins), true) + ' a tin'
            : 'Only ' + money(each, true) + ' each';

        var etaNow = etaLabel(self.etaStandard), etaNext = etaLabel(self.etaExpress);
        var etaLine = (/express/i.test(step.perk || '') && etaNow && etaNext)
          ? '<span class="eu-up__eta">Get it by ' + esc(etaNext) + ' instead of ' + esc(etaNow) + '</span>'
          : '';

        return '<div class="eu-up"' + (step.offTier ? ' data-warn="true"' : '') + '>' +
            '<span class="eu-up__eyebrow">' + esc(eyebrow) + '</span>' +
            '<b class="eu-up__headline">' + esc(headline) + '</b>' +
            '<span class="eu-up__support">' + esc(support) + '</span>' +

            '<div class="eu-up__chips">' +
              '<span class="eu-up__chip">' +
                '<b>' + step.fromTins + (step.fromTins === 1 ? ' Tin' : ' Tins') + '</b>' +
                '<span>' + money(step.rateNow) + '/tin</span>' +
              '</span>' +
              '<span class="eu-up__arrow" aria-hidden="true">&rarr;</span>' +
              '<span class="eu-up__chip eu-up__chip--to">' +
                '<b>' + step.toTins + (step.toTins === 1 ? ' Tin' : ' Tins') + '</b>' +
                '<span>' + money(step.rateNext) + '/tin</span>' +
              '</span>' +
            '</div>' +

            (step.perk
              ? '<div class="eu-up__perk">+ ' +
                  (/^(choose|get|claim)/i.test(step.perk) ? '' : 'FREE ').replace('FREE ', /free/i.test(step.perk) ? '' : 'FREE ') +
                  esc(step.perk) + etaLine + '</div>'
              : '') +

            '<button type="button" class="eu-up__cta" data-eu-next>' +
              'Choose ' + extra + ' More Flavour' + (extra > 1 ? 's' : '') +
            '</button>' +

            /* The single biggest unspoken worry at this point: does upgrading
             * throw away the flavours I already picked? */
            '<span class="eu-up__note">Your existing ' + step.fromTins +
              ' flavour' + (step.fromTins > 1 ? 's stay' : ' stays') + ' selected.</span>' +
          '</div>';
      }

      return '<div class="eu-next">' +
          '<span class="eu-next__text"><b>' + esc(step.head) + '</b>' +
            '<span>' + esc(step.sub) + '</span></span>' +
          '<button type="button" class="eu-next__cta" data-eu-next>' + esc(step.cta) + '</button>' +
        '</div>';
    },

    startNextStep: function () {
      var s = this._step;
      if (!s) return;
      var self = this;

      if (s.kind === 'complete') {
        /* With nothing to pick, the regroup is the entire offer. */
        if (s.remaining <= 0) return this.absorbLoose(s.bundleId);

        /* Otherwise absorb at the END, not here.
         *
         * Folding the loose tins in first looks tidier but leaves the customer
         * worse off the moment they hesitate: three grouped plus one loose is
         * $99.90, and merging them into an off-tier group of four is $119.80 —
         * so tapping "choose 1 more flavour" and then closing the picker costs
         * $19.90 for nothing. Deferring means the cart is untouched until they
         * actually pick, and the absorb lands in the same breath as the tin
         * that completes the tier. */
        return this.openPicker({
          kind: 'bundle', bundleId: s.bundleId, remaining: s.remaining, absorb: true
        });
      }

      this.openPicker(s.kind === 'loose'
        ? { kind: 'loose', remaining: 1 }
        : { kind: 'bundle', bundleId: s.bundleId, remaining: s.remaining, note: s.sub });
    },

    /* Stamp every ungrouped tin with a bundle id so it prices as part of that
     * bundle.
     *
     * One line per pass and a re-read between, for the same reason
     * settleBundleLabels does it: writing properties changes a line's key and
     * merges it with anything it now matches exactly, so keys captured up front
     * are stale after the first write. The customer sees a spinner for the
     * duration — this only runs on an explicit tap, and never more than a
     * handful of lines. */
    absorbLoose: function (bundleId) {
      var self = this;
      var guard = 0;

      /* No group to join means this absorb is what creates the bundle. */
      var bid = bundleId || this.looseGroupId();

      var pass = function (cart) {
        /* isTinLine matters most here. This is the one place that WRITES, and
         * stamping _bundle_id onto a premade pack would hand the tier function a
         * componentised bundle to price as loose tins. The two read-side guards
         * mean this should never be reached with a pack in hand — this is the
         * belt to their braces, because getting it wrong costs money rather than
         * pixels. */
        var loose = (cart.items || []).filter(function (l) {
          var p = l.properties || {};
          return !p._bundle_id && !p._reward && !p._protection && self.isTinLine(l);
        })[0];
        if (!loose || ++guard > 24) return cart;

        var props = {};
        Object.keys(loose.properties || {}).forEach(function (k) { props[k] = loose.properties[k]; });
        props._bundle_id = bid;

        return json('cart/change.js', {
          method: 'POST',
          body: JSON.stringify({ id: loose.key, quantity: loose.quantity, properties: props })
        }).then(pass, function () {
          /* Another write (a label repair, the gift relabel) re-keyed the line
           * under us. Re-read and carry on rather than stopping half-grouped:
           * a 1-tin "bundle" beside two singles prices worst of all. */
          return json('cart.js').then(pass);
        });
      };

      this.busy(true);
      return json('cart.js').then(pass)
        .then(function () {
          /* The id is spent — a later loose tin starts a new bundle rather than
           * silently joining the one just built. */
          self._looseGroupId = null;
          return self.refresh();
        })
        .catch(function (e) { console.error('[eu-cart] absorb loose', e); })
        .finally(function () { self.busy(false); });
    },

    /* A pick means something different depending on why the modal is open. */
    picked: function (variantId) {
      var m = this._pick || { kind: 'gift' };
      if (m.kind === 'gift') return this.addGift(variantId);
      if (m.kind === 'loose') return this.addLoose(variantId);
      return this.addToBundle(variantId);
    },

    /* Removing inside the picker has to reach the cart, not just the counter —
     * otherwise the two disagree the moment the modal closes. */
    unpick: function (variantId) {
      var self = this;
      var id = Number(variantId);

      var i = (self._picked || []).indexOf(id);
      if (i === -1) return;

      /* Optimistic, to match the + button. */
      self._picked.splice(i, 1);
      if (self._pick.kind !== 'gift') self._pick.remaining += 1;
      self.paintPicker();

      return this.enqueue(function () {
        /* Read the cart inside our turn in the queue, not before it. Adds no
         * longer refresh between taps, so this.cart does not yet know about
         * tins added moments ago — and the line we need to decrement is very
         * often one of those. By the time this runs, every earlier write has
         * committed. */
        return self.refresh().then(function (cart) {
          var line = ((cart || {}).items || []).slice().reverse().filter(function (l) {
            if (l.id !== id) return false;
            var pr = l.properties || {};
            return self._pick.kind === 'gift' ? !!pr._reward : pr._bundle_id === self._pick.bundleId;
          })[0];
          if (!line) return;
          return json('cart/change.js', {
            method: 'POST',
            body: JSON.stringify({ id: line.key, quantity: Math.max(0, line.quantity - 1) })
          });
        });
      })
        .catch(function (e) {
          console.error('[eu-cart] unpick', e);
          return self.reconcilePicker();
        });
    },

    addLoose: function (variantId) {
      var self = this;
      this.busy(true);
      return json('cart/add.js', {
        method: 'POST',
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      })
        .then(function () { return self.refresh(); })
        .then(function () {
          (self._picked = self._picked || []).push(Number(variantId));
          self.closeGiftPicker();
        })
        .catch(function (e) { console.error('[eu-cart] loose add', e); })
        .finally(function () { self.busy(false); });
    },

    /* What a group is actually costing the customer right now, in cents.
     *
     * Two kinds of discount reach a line and cart.js reports them differently.
     * A line-level one — our own bundle pricing — is already subtracted from
     * final_line_price and then *restated* in line.discounts. A cart-level one,
     * like the store's automatic "spend $200 get 20%", is listed in
     * line.discounts but is NOT yet in final_line_price. Subtracting the whole
     * discounts array double-counts the first kind; ignoring it misses the
     * second. So: start from final_line_price, and subtract only those
     * discounts that cart.js names as cart-level.
     *
     * This matters because the off-tier upgrade card compares what they pay now
     * against a tier price, and getting it wrong invents a saving that is not
     * there. */
    netOf: function (cart, pred) {
      var cartLevel = {};
      ((cart || {}).cart_level_discount_applications || []).forEach(function (a) {
        cartLevel[a.title] = true;
      });

      var net = 0;
      ((cart || {}).items || []).forEach(function (l) {
        if (!pred(l)) return;
        var v = l.final_line_price;
        (l.discounts || []).forEach(function (d) { if (cartLevel[d.title]) v -= d.amount; });
        net += v;
      });
      return net;
    },

    groupNet: function (cart, groupId) {
      return this.netOf(cart, function (l) {
        return (l.properties || {})._bundle_id === groupId;
      });
    },

    /* Tins bought outside any bundle — what the "add a flavour" strip creates.
     * Rewards and Package Protection are not tins and never count. */
    /* Money side of the same rule — a pack's price must not be folded into what
     * the loose tins are costing, or the per-tin rate on the completion card is
     * computed from a total that includes something the offer cannot touch. */
    looseNet: function (cart) {
      var self = this;
      return this.netOf(cart, function (l) {
        var p = l.properties || {};
        return !p._bundle_id && !p._reward && !p._protection && self.isTinLine(l);
      });
    },

    /* The customer-facing label for a group of this size. `Bundle` (no
     * underscore) is public: it prints on the order confirmation, the invoice
     * and the packing slip. `_bundle_tier` is private and exists for order
     * exports. Neither is ever read back for pricing — the discount function
     * counts the group itself — so a stale value costs no money, only trust. */
    bundleLabel: function (qty) {
      return qty + ' Tin' + (qty === 1 ? '' : 's');
    },

    /* Bring every bundle line into agreement with the size of the group it
     * belongs to, wherever that group has drifted.
     *
     * The upgrade path adds tins one at a time, and the lines already in the
     * cart still carry the tier they were bought at — so a 5-tin bundle taken
     * to 10 shipped with "Bundle: 5 Tins" printed on half its lines and nothing
     * on the rest. Money was right and the packing slip was wrong, which is the
     * worse of the two ways to get this wrong.
     *
     * This hangs off refresh() rather than off the picker's exits because there
     * are too many exits to catch by hand: completing an upgrade, abandoning it
     * half-way, unpicking, the drawer's own +/− steppers, removing a line, or
     * simply reopening a cart left over from a previous session. A pass that
     * runs after every refresh covers all of them, including the ones nobody
     * has thought of yet, and costs a scan of an array already in memory when
     * there is nothing to fix — which is almost always.
     *
     * One line per pass, on purpose. Rewriting properties changes a line's key
     * and merges it with any line it now matches exactly, so every key captured
     * before a write is suspect afterwards. Re-reading through refresh() is the
     * honest way to do it; the guard flag keeps the recursion to one in flight
     * and the counter stops a pathological cart rather than pacing a normal
     * one. */
    settleBundleLabels: function (cart) {
      if (this._settling) return;
      if (!this.staleBundleLine(cart)) return;

      var self = this;
      var guard = 0;

      var pass = function (c) {
        var hit = self.staleBundleLine(c);
        /* A cart that still looks wrong after this many writes is not going to
         * be fixed by more of them. Stop and leave the labels as they are —
         * the price is correct either way. */
        if (!hit || ++guard > 40) return c;

        var props = {};
        Object.keys(hit.line.properties || {}).forEach(function (k) {
          props[k] = hit.line.properties[k];
        });
        props._bundle_tier = hit.tier;
        props.Bundle = self.bundleLabel(Number(hit.tier));

        return json('cart/change.js', {
          method: 'POST',
          body: JSON.stringify({ id: hit.line.key, quantity: hit.line.quantity, properties: props })
        }).then(pass);
      };

      /* One self-contained chain that loops until the cart is clean, rather
       * than one write per render and a recursion back through refresh().
       *
       * The recursive version could be orphaned: an absorb and an upgrade
       * landing together produce overlapping renders, the second finds
       * _settling already true and returns, and when the first chain clears the
       * flag there is nothing left to continue it — so a five-tin group sat at
       * checkout still labelled 4 / 3 / undefined. Looping here cannot be
       * interrupted that way. */
      this._settling = true;
      return json('cart.js').then(pass)
        .then(function () {
          self._settling = false;
          return self.refresh();
        })
        .catch(function (e) {
          /* Cosmetic by design: the group is priced correctly either way, so a
           * failure here must not block whatever the customer was doing. */
          self._settling = false;
          console.error('[eu-cart] label settle', e);
        });
    },

    /* The first bundle line whose tier disagrees with the size of its group,
     * or null when every group agrees with itself.
     *
     * Staleness is judged on the tier alone, never on the label. The product
     * page writes its merchandising badge into the label — "5 Tins — MOST
     * POPULAR" — so matching on the label would mark an untouched 5-pack stale
     * and rewrite all five lines purely to strip a badge that was correct. */
    staleBundleLine: function (cart) {
      var groups = {};
      ((cart || {}).items || []).forEach(function (l) {
        var id = (l.properties || {})._bundle_id;
        if (!id) return;
        (groups[id] = groups[id] || []).push(l);
      });

      var found = null;
      Object.keys(groups).some(function (id) {
        var lines = groups[id];
        var want = String(lines.reduce(function (n, l) { return n + l.quantity; }, 0));
        var hit = lines.filter(function (l) {
          return (l.properties || {})._bundle_tier !== want;
        })[0];
        if (hit) { found = { line: hit, tier: want }; return true; }
        return false;
      });
      return found;
    },

    /* Adds one tin into an existing group. The discount function prices a group
     * purely on the quantity it finds, so the tier updates itself the moment the
     * count lands on 3, 5 or 10 — no price is ever sent from here.
     *
     * The label written here is the group's size *after* this add, which is the
     * right answer at every step including an abandoned upgrade — the customer
     * who stops at 7 has a 7-tin group, and it says so. */
    addToBundle: function (variantId) {
      var self = this;
      var m = this._pick;
      if (!m || m.remaining <= 0) return;

      /* The tap lands NOW. Everything the picker draws — the per-flavour
       * stepper, the progress bar, the footer count — reads from _picked and
       * m.remaining, both local, so none of it needs the server's opinion. The
       * old flow put a cart/add.js AND a cart.js round-trip in front of every
       * repaint, which is the lag: on a phone that is most of a second per tin,
       * ten times over. */
      m.remaining -= 1;
      (self._picked = self._picked || []).push(Number(variantId));

      /* Position in the group, worked out locally and in tap order. Reading it
       * from this.cart used to give two quick taps the same number, because the
       * second ran before the first had come back. */
      var after = (m.baseQty || 0) + self._picked.length;
      var lastOne = m.remaining <= 0;

      /* paintPicker, not openPicker — openPicker resets the open brand, the
       * search box and the pick counter, so every add collapsed the list and
       * sent the customer back to the top with the progress reset.
       *
       * The LAST tap repaints too. It used to be the one tap that skipped this
       * and went straight to waiting, so the tenth tin appeared to do nothing
       * while the queue drained — and that tap waits longest of all, because
       * every write queued behind the earlier taps has to land first. It now
       * shows the bundle complete and the footer working. */
      if (lastOne) this._pickSettling = true;
      this.paintPicker();

      return this.enqueue(function () {
        return json('cart/add.js', {
          method: 'POST',
          body: JSON.stringify({ items: [{
            id: Number(variantId), quantity: 1,
            properties: {
              _bundle_id: m.bundleId,
              _bundle_tier: String(after),
              Bundle: self.bundleLabel(after)
            }
          }] })
        });
      })
        .then(function () {
          /* Only the last tin pays for a refresh. Because writes are queued,
           * by the time this one resolves every earlier tin has committed, so
           * one read here sees the finished bundle. */
          if (!lastOne) return;
          self.busy(true);
          return self.refresh()
            .then(function (cart) {
              self._pickSettling = false;
              self.closeGiftPicker();

              /* The tier is reached counting the loose tins, so fold them in
               * now — this is the step that turns four grouped tins plus a
               * stray into a five-pack at $109.95. */
              var done = m.absorb ? self.absorbLoose(m.bundleId) : Promise.resolve(cart);

              return done.then(function () {
                var live = self.cart || cart;
                var st = self.bundleState(live);
                track('EUBundleUpgraded', {
                  tins: st.group ? st.group.qty : null,
                  value: (live.total_price || 0) / 100, currency: 'AUD'
                });
              });
            })
            .then(function () { self.busy(false); }, function (e) { self.busy(false); throw e; });
        })
        .catch(function (e) {
          console.error('[eu-cart] bundle add', e);
          self._pickSettling = false;
          self.busy(false);
          /* Local state is now ahead of the cart. Re-derive it rather than
           * leaving the customer counting tins that were never added. */
          return self.reconcilePicker();
        });
    },

    /* --- upsells ---------------------------------------------------------- */

    /* Anything in stock that isn't already in the cart. Adding one is an
     * ordinary full-price line — no bundle id — so it never disturbs a group's
     * quantity and the discount function ignores it. It does count toward the
     * reward thresholds, which is rather the point. */
    /* Stock floor for a recommendation.
     *
     * `cap >= 1` was enough to prove a tin is buyable, but not enough to be
     * worth suggesting: the last unit of a flavour sells, the next customer to
     * open the drawer gets recommended something already gone, and the strip
     * spends its credibility on stock we were about to lose anyway. */
    UPSELL_MIN_STOCK: 5,

    upsellCandidates: function (cart) {
      var inCart = {};
      (cart.items || []).forEach(function (l) { inCart[l.id] = true; });

      var min = this.UPSELL_MIN_STOCK;
      var allow = this.upsellHandles;
      var pool = this.catalogue().filter(function (t) {
        if (!t.available || t.cap < min) return false;
        if (inCart[t.id]) return false;
        return !allow.length || allow.indexOf(t.handle) !== -1;
      });

      // A configured list is an editorial decision, so it keeps its order.
      if (allow.length) {
        pool.sort(function (a, b) { return allow.indexOf(a.handle) - allow.indexOf(b.handle); });
      }
      return pool;
    },

    /* One from the brand they are already buying, the rest from brands they are
     * not.
     *
     * Affinity and discovery out of a single rule, which is as much machinery as
     * this is worth. Deliberately NOT modelled: margin, because every tin is the
     * same $6 landed against the same $29.95, so the signal carries no
     * information; and flavour-profile matching, because most of the tasting
     * notes are still guesses awaiting a real taste test, and merchandising off
     * unverified data is how you recommend a fourth mint. */
    orderByBrandMix: function (pool, cart) {
      var self = this;
      var counts = {};
      (cart.items || []).forEach(function (l) {
        var t = self.tinByVariant(l.id);
        if (t) counts[t.brand] = (counts[t.brand] || 0) + l.quantity;
      });

      var top = null;
      Object.keys(counts).forEach(function (b) {
        if (!top || counts[b] > counts[top]) top = b;
      });
      if (!top) return pool;

      var same = pool.filter(function (t) { return t.brand === top; });
      var other = pool.filter(function (t) { return t.brand !== top; });

      /* Round-robin the discovery slots across brands, so three cards are three
       * brands rather than one favourite and two of whatever happened to be
       * next in the catalogue. Deterministic on purpose — a strip that
       * reshuffles between repaints is one you cannot actually tap. */
      var byBrand = {}, order = [];
      other.forEach(function (t) {
        if (!byBrand[t.brand]) { byBrand[t.brand] = []; order.push(t.brand); }
        byBrand[t.brand].push(t);
      });
      var spread = [], n = 0, took = true;
      while (took) {
        took = false;
        for (var i = 0; i < order.length; i++) {
          var list = byBrand[order[i]];
          if (list.length > n) { spread.push(list[n]); took = true; }
        }
        n++;
      }

      return same.slice(0, 1).concat(spread, same.slice(1));
    },

    /* Which ones to show. Held across repaints: a suggestion that reshuffles
     * every time a quantity changes is impossible to actually tap, and it makes
     * the drawer feel broken. Only re-picks when a held one stops being valid —
     * added to the cart, or sold out. */
    pickUpsells: function (cart) {
      var pool = this.upsellCandidates(cart);
      var byId = {};
      pool.forEach(function (t) { byId[t.id] = t; });

      /* A tin that was just added is no longer a candidate — it is in the cart —
       * but it has to survive one more repaint so the tap has a visible result
       * before the card goes. */
      if (this._justAdded && !byId[this._justAdded]) {
        var added = this.tinByVariant(this._justAdded);
        if (added) byId[added.id] = added;
      }

      var kept = (this._upsells || []).filter(function (id) { return byId[id]; });

      if (kept.length < this.upsellCount) {
        var rest = pool.filter(function (t) { return kept.indexOf(t.id) === -1; });
        /* A configured handle list is already in the order someone chose, so
         * only re-rank when we are choosing for ourselves. */
        if (!this.upsellHandles.length) rest = this.orderByBrandMix(rest, cart);
        kept = kept.concat(rest.slice(0, this.upsellCount - kept.length).map(function (t) { return t.id; }));
      }

      this._upsells = kept.slice(0, this.upsellCount);
      return this._upsells.map(function (id) { return byId[id]; });
    },

    addUpsell: function (variantId) {
      var self = this;
      this.busy(true);
      return json('cart/add.js', {
        method: 'POST',
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      })
        .then(function () {
          var t = self.tinByVariant(Number(variantId));
          track('EUUpsellAdded', {
            content_ids: [String(variantId)],
            content_name: t ? t.brand + ' ' + t.flavour : String(variantId),
            value: t ? t.price / 100 : 0, currency: 'AUD'
          });

          /* Hold the card in an added state for a beat.
           *
           * Adding removes the tin from the candidate pool, so the card simply
           * vanished and the strip shuffled up — the one visible response to
           * the tap was the thing the customer tapped disappearing. */
          self._justAdded = Number(variantId);
          clearTimeout(self._justAddedT);
          self._justAddedT = setTimeout(function () {
            self._justAdded = null;
            if (self.cart) self.render(self.cart);
          }, 1200);

          return self.refresh();
        })
        .catch(function (e) { console.error('[eu-cart] upsell add', e); })
        .finally(function () { self.busy(false); });
    },

    /* Returns markup rather than painting its own region.
     *
     * As a fixed region between the list and the footer it cost 105px of a
     * ~836px drawer permanently, and with the footer at 460px that left the
     * cart items 73px — you could not see what you were buying. Upsells are
     * discovery, not decision, so they belong in the part that scrolls. */
    upsellHtml: function (cart) {
      if (!this.upsellOn || !(cart.items || []).length) return '';

      var st = this.bundleState(cart);
      var tiers = this.tiersAsc();

      /* Top of the ladder. Every reward is unlocked and there is no larger
       * bundle to reach, so the only thing left to do is check out. One more
       * tin here is worth a shade under a dollar in expectation, against a
       * ~$270 order you do not want to give anyone a reason to reconsider. */
      if (st.group && st.group.qty >= tiers[tiers.length - 1]) return '';

      /* Never sit a full-price tin next to an offer that beats it.
       *
       * The completion card is offering a fifth tin for $10.05, and the
       * undercutting off-tier card is offering a tenth for less than the
       * customer is already paying. A $29.95 single beside either is a worse
       * deal wearing the same shape, and showing both only makes the good one
       * harder to see. */
      var step = this._step;
      if (step && (step.kind === 'complete' || step.undercuts)) return '';

      var picks = this.pickUpsells(cart);
      /* One lonely card reads as a leftover rather than a choice. */
      if (picks.length < 2) return '';

      /* The one place the strip earns a headline of its own. At 10 tins the
       * next $29.95 clears $219 and brings a free tin with it, so the customer
       * gets two tins for one — worth saying plainly, and true. */
      var self = this;
      var gift = this._giftTierAmount || 0;
      var paid = this._giftBasis || 0;
      /* giftsAllowed, not this.gifts: a G10 token on a 10-pack already has its
       * tin, and "add one more to unlock your gift" would be asking for
       * $29.95 to get what is sitting in the slot above. Identical with no
       * token. */
      var unlocksGift = gift > 0 && !this.giftsAllowed() &&
                        paid < gift && paid + 2995 >= gift;

      var head = unlocksGift ? 'Add one more tin to unlock your gift!' : this.upsellTitle;

      return '<div class="eu-ups"' + (unlocksGift ? ' data-unlock="true"' : '') + '>' +
        '<div class="eu-ups__head">' + esc(head) + '</div>' +
        '<div class="eu-ups__strip">' + picks.map(function (t) {
          var added = t.id === self._justAdded;
          return '<button type="button" class="eu-ups__card"' +
                   (added ? ' data-added="true" disabled' : ' data-eu-upsell-add="' + t.id + '"') +
                   ' aria-label="' + (added ? esc(t.flavour) + ' added' : 'Add ' + esc(t.flavour) + ' to cart') + '">' +
            (t.image ? '<img src="' + esc(t.image) + '" alt="" loading="lazy" width="40" height="40">' : '<span class="eu-ups__img"></span>') +
            '<span class="eu-ups__body">' +
              '<b>' + esc(t.flavour) + '</b>' +
              '<i>' + (added ? 'Added to cart' : esc(t.brand) + ' · ' + money(t.price)) + '</i>' +
            '</span>' +
            '<span class="eu-ups__add" aria-hidden="true">' + (added ? '&#10003;' : '+') + '</span>' +
          '</button>';
        }).join('') + '</div>' +
      '</div>';
    },

    /* --- discount codes -------------------------------------------------- */

    /* Shopify's Ajax cart cannot apply a discount code. /discount/{code} sets it
     * on the session and it then appears in cart.js. There is no Ajax removal
     * endpoint either, so "remove" applies an empty code — crude, but the only
     * option short of sending the customer to a checkout URL. */
    /* Read-only. Confirms a code from a campaign link took effect; the amount
     * itself is already itemised in the totals. No input, and no remove button
     * — the Ajax cart has no endpoint to remove a code anyway, so the old one
     * worked by applying an empty code, which was always a bit of a trick. */
    /* "Got A Code From A Mate?" (26 Oct 2026). A quiet link, not an empty
     * discount box, so it doesn't send shoppers hunting for codes; it only
     * opens when tapped. Built here rather than in the snippet so the drawer
     * file is the only thing that changes. */
    renderRefCode: function (cart) {
      var self = this;
      var foot = this.el.querySelector('[data-eu-cart-foot]');
      if (!foot) return;
      var cur = (cart || {}).currency;
      if (cur && typeof cur === 'object') cur = cur.iso_code;
      var box = foot.querySelector('[data-eu-ref]');
      if (cur && cur !== 'AUD') { if (box) box.hidden = true; return; }
      if (!box) {
        if (!document.getElementById('eu-ref-css')) {
          var st = document.createElement('style');
          st.id = 'eu-ref-css';
          st.textContent =
            '.eu-ref{margin:0 0 14px;text-align:center;font-size:14px}' +
            '.eu-ref__toggle{background:none;border:0;padding:4px 0;font:inherit;font-weight:600;color:inherit;text-decoration:underline;text-underline-offset:3px;cursor:pointer}' +
            '.eu-ref__row{display:flex;gap:8px;margin-top:6px}' +
            '.eu-ref__input{flex:1;min-width:0;height:46px;padding:0 14px;border:1.5px solid #d4d4d4;border-radius:10px;font:inherit;font-size:16px;font-weight:600;letter-spacing:1px;text-transform:uppercase;background:#fff;color:#111}' +
            '.eu-ref__input:focus{outline:none;border-color:#111}' +
            '.eu-ref__apply{height:46px;padding:0 20px;border:0;border-radius:10px;background:#111;color:#fff;font:inherit;font-weight:700;cursor:pointer}' +
            '.eu-ref__err{margin:6px 0 0;color:#b42318;font-size:13px;text-align:left}' +
            '.eu-ref__help{margin:0 0 8px;font-size:13px;color:#666;text-align:left}' +
            '.eu-refpend{margin:0 0 12px;padding:12px 14px;border-radius:12px;background:#111;color:#fff;text-align:left}' +
            '.eu-refpend b{display:block;font-size:14px;margin-bottom:8px}' +
            '.eu-refpend__row{display:flex;gap:8px;flex-wrap:wrap}' +
            '.eu-refpend__go{flex:1 1 auto;height:42px;border:0;border-radius:8px;background:#e3fc03;color:#111;font:inherit;font-weight:800;cursor:pointer}' +
            '.eu-refpend__skip{flex:1 1 auto;height:42px;border:1px solid #555;border-radius:8px;background:none;color:#fff;font:inherit;font-weight:600;cursor:pointer}' +
            '[data-eu-ref-pending="true"] .eu-cart__express{position:relative}' +
            '[data-eu-ref-pending="true"] .eu-cart__express:after{content:"";position:absolute;inset:0;z-index:5;cursor:pointer}' +
            '.eu-ref__on{display:inline-flex;align-items:center;gap:10px;padding:8px 12px;border-radius:999px;background:#f1f1f1;font-weight:600}' +
            '.eu-ref__on b{letter-spacing:.5px}' +
            '.eu-ref__remove{background:none;border:0;padding:0;font:inherit;font-size:13px;color:#666;text-decoration:underline;cursor:pointer}';
          document.head.appendChild(st);
        }
        var coForm = foot.querySelector('.eu-cart__checkoutform');
        if (coForm) {
          var stopIfPending = function (e) {
            if (!self.refPending()) return;
            var hit = e.type === 'submit' || (e.target.closest && (e.target.closest('.eu-cart__checkout') || e.target.closest('.eu-cart__express')));
            if (!hit) return;
            e.preventDefault(); e.stopPropagation();
            self._refAsk = true;
            self._refAskCheckout = e.type === 'submit' || !!(e.target.closest && e.target.closest('.eu-cart__checkout'));
            self.renderRefPending();
            var b = foot.querySelector('[data-eu-refpend]');
            if (b && b.scrollIntoView) b.scrollIntoView({ block: 'nearest' });
          };
          coForm.addEventListener('click', stopIfPending, true);
          coForm.addEventListener('submit', stopIfPending, true);
          foot.addEventListener('click', function (e) {
            if (e.target.closest('[data-eu-refpend-go]')) { self._refAsk = false; self.renderRefPending(); self.openGiftPicker(); }
            if (e.target.closest('[data-eu-refpend-skip]')) {
              self._refSkip = self.offerToken; self._refAsk = false; self.renderRefPending();
              if (self._refAskCheckout) { var co = foot.querySelector('.eu-cart__checkout'); if (co) co.click(); }
            }
          });
        }
        box = document.createElement('div');
        box.className = 'eu-ref';
        box.setAttribute('data-eu-ref', '');
        var totals = foot.querySelector('.eu-cart__totals');
        foot.insertBefore(box, totals || foot.firstChild);
        box.addEventListener('click', function (e) {
          if (e.target.closest('[data-eu-ref-toggle]')) { self._refOpen = true; self._refHow = false; self.renderRefCode(self.cart); var i = box.querySelector('input'); if (i) i.focus(); }
          if (e.target.closest('[data-eu-ref-apply]')) self.applyRefCode(box.querySelector('input').value);
          if (e.target.closest('[data-eu-ref-remove]')) self.applyRefCode('');
          if (e.target.closest('[data-eu-refhow]')) { self._refHow = !self._refHow; self.renderRefCode(self.cart); }
        });
        box.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' && e.target.matches('input')) { e.preventDefault(); self.applyRefCode(e.target.value); }
        });
      }
      box.hidden = false;
      var have = this.offerRef && this.offerDead() !== this.offerToken ? this.offerToken : '';
      if (have) {
        box.innerHTML = '<span class="eu-ref__on"><span>\u2713 Code <b>' + esc(have) + '</b> Added</span>' +
          '<button type="button" class="eu-ref__remove" data-eu-ref-remove>Remove Code</button></span>';
      } else if (this._refOpen) {
        box.innerHTML = '<p class="eu-ref__help">Use a code from a mate or a reward email.</p>' +
          '<div class="eu-ref__row">' +
            '<input class="eu-ref__input" type="text" inputmode="text" autocapitalize="characters" autocomplete="off" spellcheck="false" placeholder="e.g. JORDAN7759" aria-label="Code from a mate" value="' + esc(this._refDraft || '') + '">' +
            '<button type="button" class="eu-ref__apply" data-eu-ref-apply>Apply</button>' +
          '</div>' + (this._refErr ? '<p class="eu-ref__err" role="alert">' + esc(this._refErr) + '</p>' : '') +
          this.refHowHtml();
      } else {
        box.innerHTML = '<button type="button" class="eu-ref__toggle" data-eu-ref-toggle>Got A Code From A Mate?</button>';
      }
    },

    /* The bottom-right "Got A Code From A Mate?" button, on every page (26 Oct
     * 2026). A mate texts a code; this is where it goes, before anything is in
     * the cart. Entered with an empty cart, the code is saved on the cart and
     * waits; with 3+ tins already in, the drawer and flavour picker open. */
    renderRefFab: function (cart) {
      var self = this;
      if (this.inline) return;
      var cur = (cart || {}).currency;
      if (cur && typeof cur === 'object') cur = cur.iso_code;
      var fab = document.querySelector('[data-eu-reffab]');
      if (cur && cur !== 'AUD') { if (fab) fab.hidden = true; return; }
      if (!document.getElementById('eu-reffab-css')) {
        var st = document.createElement('style');
        st.id = 'eu-reffab-css';
        st.textContent =
          '.eu-reffab{position:fixed;right:16px;bottom:calc(16px + env(safe-area-inset-bottom));z-index:9990;font-family:inherit}' +
          'html.eu-cart-is-open .eu-reffab{display:none}' +
          '.eu-reffab__pill{display:inline-flex;align-items:center;gap:8px;height:48px;padding:0 18px 0 14px;border:0;border-radius:999px;background:#e3fc03;color:#111;font:inherit;font-size:15px;font-weight:800;box-shadow:0 6px 24px rgba(0,0,0,.35);cursor:pointer}' +
          '.eu-reffab__pill svg{flex:none}' +
          '.eu-reffab__pill.is-saved{background:#111;color:#e3fc03;box-shadow:0 0 0 2px #e3fc03,0 6px 24px rgba(0,0,0,.35)}' +
          '.eu-reffab__sheet{position:absolute;right:0;bottom:60px;width:340px;max-width:calc(100vw - 32px);padding:20px;border-radius:16px;background:#fff;color:#111;box-shadow:0 12px 40px rgba(0,0,0,.28)}' +
          '.eu-reffab__sheet{max-height:80vh;overflow-y:auto;-webkit-overflow-scrolling:touch}' +
          '.eu-reffab__x{position:absolute;top:10px;right:12px;border:0;background:none;font-size:24px;line-height:1;color:#555;cursor:pointer}' +
          '.eu-reffab__h{margin:0 0 4px;font-size:19px;font-weight:800}' +
          '.eu-reffab__p{margin:0 0 14px;font-size:15px;color:#444}' +
          '.eu-reffab__ok{margin:0 0 14px;font-size:15px}' +
          '.eu-reffab__ok b{display:block;font-size:17px;margin-bottom:4px}' +
          '.eu-reffab__go{display:block;width:100%;height:46px;border:0;border-radius:10px;background:#e3fc03;color:#111;font:inherit;font-weight:800;cursor:pointer}' +
          '@media (max-width:600px){.eu-reffab__sheet{position:fixed;left:0;right:0;bottom:0;width:auto;max-width:none;border-radius:18px 18px 0 0;padding:22px 16px calc(22px + env(safe-area-inset-bottom))}}';
        document.head.appendChild(st);
      }
      if (!fab) {
        fab = document.createElement('div');
        fab.className = 'eu-reffab';
        fab.setAttribute('data-eu-reffab', '');
        document.body.appendChild(fab);
        fab.addEventListener('click', function (e) {
          if (e.target.closest('[data-eu-reffab-pill]')) { self._refSheet = !self._refSheet; self.renderRefFab(self.cart); var i = fab.querySelector('input'); if (i) i.focus(); return; }
          if (e.target.closest('[data-eu-reffab-x]')) { self.closeRefSheet(); return; }
          if (e.target.closest('[data-eu-refhow]')) { self._refHow = !self._refHow; self.renderRefFab(self.cart); return; }
          if (e.target.closest('[data-eu-reffab-apply]')) { self.applyRefCode(fab.querySelector('input').value); return; }
          if (e.target.closest('[data-eu-reffab-cart]')) { self.closeRefSheet(); self.open(); return; }
          if (e.target.closest('[data-eu-reffab-shop]')) { self.closeRefSheet(); if (!/\/collections\//.test(location.pathname)) location.href = SHOP_URL; return; }
        });
        fab.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' && e.target.matches('input')) { e.preventDefault(); self.applyRefCode(e.target.value); }
        });
      }
      // Claimed: the free tin is in the cart, so the button has done its job.
      var claimed = this.offerRef && this.giftLines(cart || {}).length > 0;
      var have = this.offerRef && this.offerDead() !== this.offerToken ? this.offerToken : '';
      if (claimed && !this._refSheet) { fab.hidden = true; return; }
      fab.hidden = false;
      var gift = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="8" width="18" height="13" rx="2"/><path d="M12 8v13M3 12h18M12 8c-2-4-6-4-6-1.5S9 8 12 8zm0 0c2-4 6-4 6-1.5S15 8 12 8z"/></svg>';
      var pill = have
        ? '<button type="button" class="eu-reffab__pill is-saved" data-eu-reffab-pill>' + gift + 'Free Tin Code Added</button>'
        : '<button type="button" class="eu-reffab__pill" data-eu-reffab-pill>' + gift + 'Got A Code From A Mate?</button>';
      var sheet = '';
      if (this._refSheet) {
        var tins = this.paidTins(cart || {});
        var inner;
        if (have) {
          inner = '<p class="eu-reffab__ok"><b>\u2713 Code ' + esc(have) + ' Added</b>' +
            (tins >= 3 ? 'Now choose your free flavour in the cart.' : 'Buy 3 or more tins, then choose your free flavour in the cart.' + (tins ? ' Add ' + (3 - tins) + ' more.' : '')) +
            (this.offerRef === 'reward' ? ' Check out with the email your reward was sent to.' : '') + '</p>' +
            (tins >= 3 || tins > 0
              ? '<button type="button" class="eu-reffab__go" data-eu-reffab-cart>View Cart</button>'
              : '<button type="button" class="eu-reffab__go" data-eu-reffab-shop>Shop Tins</button>');
        } else {
          inner = '<p class="eu-reffab__h">Got A Code From A Mate?</p>' +
            '<p class="eu-reffab__p">Buy 3 or more tins, then choose 1 extra tin free.</p>' +
            '<p class="eu-ref__help">Use a code from a mate or a reward email.</p>' +
            '<div class="eu-ref__row">' +
              '<input class="eu-ref__input" type="text" autocapitalize="characters" autocomplete="off" spellcheck="false" placeholder="e.g. JORDAN7759" aria-label="Code from a mate" value="' + esc(this._refDraft || '') + '">' +
              '<button type="button" class="eu-ref__apply" data-eu-reffab-apply>Apply</button>' +
            '</div>' + (this._refErr ? '<p class="eu-ref__err" role="alert">' + esc(this._refErr) + '</p>' : '') +
            this.refHowHtml();
        }
        sheet = '<div class="eu-reffab__sheet" role="dialog" aria-label="Got a code from a mate?">' +
          '<button type="button" class="eu-reffab__x" data-eu-reffab-x aria-label="Close">&times;</button>' + inner + '</div>';
      }
      fab.innerHTML = sheet + pill;
      // The in-drawer box styles (input, Apply, error) are shared.
      this.renderRefCode && this.el && this.el.querySelector('[data-eu-cart-foot]') && !document.getElementById('eu-ref-css') && this.renderRefCode(cart || {});
    },

    /* "How Do I Get A Free Tin?" (Matthew, 26 Oct 2026): plenty of people who
     * tap Got A Code From A Mate? won't have one, so the box explains Give A
     * Tin, Get A Tin and how to earn a code of their own. */
    refHowHtml: function () {
      if (!document.getElementById('eu-refhow-css')) {
        var st = document.createElement('style');
        st.id = 'eu-refhow-css';
        st.textContent =
          '.eu-refhow-t{display:inline-block;margin-top:12px;background:none;border:0;padding:0;font:inherit;font-size:14px;font-weight:600;color:inherit;text-decoration:underline;text-underline-offset:3px;cursor:pointer}' +
          '.eu-refhow{margin-top:12px;padding:14px 16px;border-radius:12px;background:#f4f4f1;color:#111;text-align:left}' +
          '.eu-refhow__h{margin:0 0 10px;font-size:15px;font-weight:800}' +
          '.eu-refhow__s{margin:0 0 10px;font-size:14px;line-height:1.45;color:#444}' +
          '.eu-refhow__s b{display:block;color:#111;font-size:14px;margin-bottom:1px}' +
          '.eu-refhow__fine{margin:4px 0 0;font-size:12px;color:#666}' +
          '.eu-refhow li{position:relative;padding:0 0 10px 34px;counter-increment:eurh;font-size:14px;line-height:1.45;color:#444}' +
          '.eu-refhow li:last-child{padding-bottom:0}' +
          '.eu-refhow li:before{content:counter(eurh);position:absolute;left:0;top:0;width:24px;height:24px;border-radius:12px;background:#e3fc03;color:#111;font-size:12px;font-weight:800;line-height:24px;text-align:center}' +
          '.eu-refhow li b{display:block;color:#111;font-size:14px}';
        document.head.appendChild(st);
      }
      var open = !!this._refHow;
      return '<button type="button" class="eu-refhow-t" data-eu-refhow aria-expanded="' + open + '">' +
          (open ? 'Hide' : 'How Do I Get A Free Tin?') + '</button>' +
        (open
          ? '<div class="eu-refhow"><p class="eu-refhow__h">Give A Tin, Get A Tin</p>' +
              '<p class="eu-refhow__s"><b>Got A Code?</b>Enter it above. Buy 3 or more tins, then choose 1 extra tin free in your cart. Bundles and single tins both count.</p>' +
              '<p class="eu-refhow__s"><b>No Code Yet?</b>The first time you order, we email you your own code to share with mates. It goes to customers subscribed to our emails.</p>' +
              '<p class="eu-refhow__s"><b>Earn A Tin By Sharing</b>When a mate uses your code on their first order of 3 or more tins, you earn 1 free tin for your next order of 3 or more tins.</p>' +
              '<p class="eu-refhow__fine">One free tin per order. You can use a mate\'s code once.</p>' +
            '</div>'
          : '');
    },

    closeRefSheet: function () {
      this._refSheet = false;
      this._refHow = false;
      this.renderRefFab(this.cart);
    },

    applyRefCode: function (raw) {
      var self = this;
      var code = String(raw || '').trim().toUpperCase().replace(/\s+/g, '');
      this._refDraft = code;
      if (code && !this.refKind(code)) {
        this._refErr = /\d/.test(code)
          ? "That doesn't look like one of our codes. Check every letter and number and try again."
          : 'Enter the full code, including the numbers.';
        this.renderRefCode(this.cart);
        this.renderRefFab(this.cart);
        return Promise.resolve(this.cart);
      }
      this._refErr = '';
      if (code && this.offerDead() === code) this.offerDead(null);
      this._refApplying = true;
      this.busy(true);
      // The attribute drives the drawer; the discount code makes checkout pay.
      // Removing the code takes the code's free tin out in the SAME request:
      // left to syncGifts, the tin was removed in a second request that raced
      // the refresh and surfaced a false "That didn't work" (26 Oct 2026).
      var body = { attributes: { _eu_ref: code }, discount: code };
      if (!code && this.offerGiftOnly()) {
        var updates = {};
        this.giftLines(this.cart || {}).forEach(function (l) { updates[l.key] = 0; });
        if (Object.keys(updates).length) body.updates = updates;
      }
      return json('cart/update.js', { method: 'POST', body: JSON.stringify(body) })
        .catch(function () { return json('cart/update.js', { method: 'POST', body: JSON.stringify({ attributes: { _eu_ref: code }, updates: body.updates || {} }) }); })
        .then(function () { return self.refresh(); })
        .then(function (cart) {
          self._refOpen = false;
          return Promise.resolve(code ? self.autoBundleForCode(cart || self.cart, true) : null).then(function (c2) { return c2 || cart; });
        })
        .then(function (cart) {
          if (code) track('EURefCodeAdded', { kind: self.refKind(code) });
          if (!code) self.notify(body.updates ? 'Code and free tin removed.' : 'Code removed.', 4000);
          self.render(cart || self.cart);
          // Straight to the flavour picker when the tin is already unlocked.
          if (code && self.offerGift && self.giftLines(self.cart).length < self.giftsAllowed()) {
            self.closeRefSheet();
            self.open();
            self.openGiftPicker();
          } else if (code) {
            self._refSaved = true;
          }
          self.renderRefFab(self.cart);
          return cart;
        })
        .catch(function (e) { console.error('[eu-cart] ref code', e); })
        .finally(function () { self._refApplying = false; self.busy(false); });
    },

    renderDiscounts: function (cart) {
      var wrap = this.el.querySelector('[data-eu-code]');
      if (!wrap) return;
      var ref = this.offerRef ? this.offerToken : '';
      // The referral code already has its own line above the totals.
      var codes = (cart.discount_codes || []).filter(function (d) { return d.applicable && String(d.code).toUpperCase() !== ref; });
      wrap.hidden = !codes.length;
      wrap.innerHTML = codes.map(function (d) {
        return '<span class="eu-code__chip">' + esc(d.code) + ' applied</span>';
      }).join('');
    },

    /* --- render --------------------------------------------------------- */

    render: function (cart) {
      var self = this;
      if (!this.el || !cart) return;
      this.cart = cart;

      /* First, so the rewards bar, the gift slot and the gift sync all read
       * the same answer about any ?offer= token on the cart. */
      this.offerState(cart);

      /* Let the collection bar/tray mirror cart edits made in here — dispatched
       * on every render so both empty and populated states stay in sync. */
      try { document.dispatchEvent(new CustomEvent('eu:cart-updated', { detail: cart })); } catch (e) {}

      /* Every path that changes the cart lands here, which makes this the one
       * place a drifted bundle label can be caught without enumerating them. */
      this.settleBundleLabels(cart);

      /* Paid tins only. item_count counts the free gift and Package Protection
       * too, so a 10-pack with its reward read "Cart • 12" — the number stopped
       * matching the bundle the customer thinks they bought. */
      var title = this.el.querySelector('[data-eu-cart-title]');
      if (title) {
        var tins = (cart.items || []).reduce(function (a, l) {
          var pr = l.properties || {};
          return (pr._reward || pr._protection) ? a : a + l.quantity;
        }, 0);
        title.textContent = 'Cart • ' + tins;
      }

      var body = this.el.querySelector('[data-eu-cart-body]');
      var foot = this.el.querySelector('[data-eu-cart-foot]');

      if (!cart.item_count) {
        // A dead end with no way out is a bounce. Give it a door.
        body.innerHTML = '<div class="eu-cart__empty">' +
          '<b>Your Cart Is Empty</b>' +
          '<span>Build your own bundle to save more per pouch and unlock free shipping.</span>' +
          '<a class="eu-cart__emptycta" href="' + esc(SHOP_URL) + '">Shop Tins</a>' +
        '</div>';
        foot.hidden = true;

        /* This branch returns early, so every other region has to be cleared
         * by hand — otherwise emptying the cart leaves the reward bar still
         * promising free postage and the upsell strip still offering to add to
         * an order that no longer exists. */
        ['[data-eu-rewards]', '[data-eu-gift]',
         '[data-eu-protection]', '[data-eu-cart-msg]'].forEach(function (sel) {
          var el = self.el.querySelector(sel);
          if (el) el.hidden = true;
        });
        this.closeGiftPicker();
        this.renderRefFab(cart);
        return;
      }
      foot.hidden = false;

      // Split into bundle groups and loose lines, preserving cart order.
      var groups = [];
      var index = {};
      var loose = [];
      (cart.items || []).forEach(function (l) {
        /* Package Protection is controlled by the checkbox in the footer, so it
         * must not also appear as a line. It was showing in both places with
         * two different ways to remove it — invisible while the product had no
         * image, obvious the moment it got one. */
        if ((l.properties || {})._protection) return;
        var id = (l.properties || {})._bundle_id;
        if (!id) { loose.push(l); return; }
        if (!index[id]) { index[id] = { id: id, lines: [], label: (l.properties || {}).Bundle || 'Bundle' }; groups.push(index[id]); }
        index[id].lines.push(l);
      });

      /* Rewards first: nextStepHtml asks whether the upgrade clears the next
       * threshold, and that answer comes from the entitlement pass. Building
       * the body first meant the eyebrow was always one render behind. It also
       * runs before the rows now, because the free tin's badge reads
       * this.gifts (via offerGiftOnly) to say which offer is paying for it. */
      this.renderRewards(cart);
      var html = groups.map(this.groupHtml, this).join('') + loose.map(this.lineHtml, this).join('');
      body.innerHTML = html + this.nextStepHtml(cart) + this.upsellHtml(cart);

      var saved = cart.total_discount || 0;
      var gross = cart.original_total_price || (cart.items_subtotal_price + saved);
      foot.querySelector('[data-eu-cart-subtotal]').textContent = money(gross);

      /* Three different things reduce the price and they must not be conflated.
       * Line-level discounts come from our own function (bundle tiers, and free
       * tins zeroed out). Cart-level discounts are order discounts and codes,
       * which never touch final_line_price — subtracting them from a line total
       * would label someone's 20% code as a "bundle saving". */
      var bundleSaved = 0, giftSaved = 0, giftQty = 0;
      (cart.items || []).forEach(function (l) {
        var d = l.original_line_price - l.final_line_price;
        if ((l.properties || {})._reward) { giftSaved += d; giftQty += l.quantity; }
        else bundleSaved += d;
      });

      /* Subtotal and a separate savings line cost three rows to say one thing.
       * What the customer needs is what they pay and what they saved — the full
       * breakdown is a tap away at checkout. */
      var saveRow = foot.querySelector('[data-eu-cart-saverow]');
      var totalSaved = bundleSaved + giftSaved +
        (cart.cart_level_discount_applications || []).reduce(function (a, d) {
          return a + (d.total_allocated_amount || 0); }, 0);
      /* Ticking a $3 add-on has nothing to do with what the bundle saved, so the
       * line must not appear and disappear underneath it. Once there is a saving
       * in the cart it is a fact about that cart and it stays put.
       *
       * Gated on the number actually printed, not on the bundle portion of it.
       * An off-tier group draws no line discount at all, so a 12-tin cart saving
       * $71.88 through the store's automatic discount had the row hidden while
       * showing the discounted total — the money came off and nothing said why. */
      saveRow.hidden = totalSaved <= 0;
      if (totalSaved > 0) foot.querySelector('[data-eu-cart-saved]').textContent = "You're Saving " + money(totalSaved);

      /* The separate "Free tin − $29.95" row stays hidden (Matthew, 6 Oct 2026).
       * It had no styling and sat loose beside Package Protection, and the free
       * tin's value is already inside "You're Saving" above. */
      var giftRow = foot.querySelector('[data-eu-cart-giftrow]');
      if (giftRow) giftRow.hidden = true;

      // Order discounts and codes, each named, so the total always adds up.
      var other = foot.querySelector('[data-eu-cart-otherdisc]');
      if (other) {
        other.innerHTML = (cart.cart_level_discount_applications || []).map(function (d) {
          return '<div class="eu-cart__row eu-cart__row--save">' +
            '<span>' + esc(d.title || d.key || 'Discount') + '</span>' +
            '<span>− ' + money(d.total_allocated_amount) + '</span>' +
          '</div>';
        }).join('');
      }

      foot.querySelector('[data-eu-cart-total]').textContent = money(cart.total_price);

      /* The price belongs on the button. Making someone look back up to the
       * totals to confirm what they are about to be charged is friction at the
       * exact moment you don't want any. */
      var btn = foot.querySelector('[data-eu-checkout-label]');
      if (btn) btn.innerHTML = money(cart.total_price) + ' \u203a';
      this.renderDiscounts(cart);
      this.renderRefCode(cart);
      this.renderRefFab(cart);
      this.renderRefPending();
      this.renderGiftSlot(cart);
      this.renderProtection(cart);
      this.syncGifts(cart);
      this.autoBundleForCode(cart);
    },

    /* Audit, 26 Oct 2026: someone following "buy 3 or more tins" often adds
     * single tins and pays $89.85 unless they spot "Regroup My 3 Tins". While a
     * mate's or reward code is active, apply that same regroup automatically:
     * same tins, same free tin, only the price changes. Once per cart shape, and
     * never while a picker is open. */
    autoBundleForCode: function (cart, now) {
      if (!this.offerRef || this.offerDead() === this.offerToken) return null;
      if (!now && (this._refApplying || this._absorbing)) return null;
      var st = this._step;
      if (!st || st.kind !== 'complete' || st.remaining > 0) return null;
      if (this.bundlePickOpen() || (this._pending || 0) > 0) return null;
      var sig = (cart.items || []).map(function (l) { return l.key; }).join('|');
      if (this._autoBundleSig === sig) return null;
      this._autoBundleSig = sig;
      var self = this;
      var run = function () {
        self._absorbing = true;
        return Promise.resolve(self.absorbLoose(st.bundleId)).finally(function () { self._absorbing = false; });
      };
      if (now) return run();
      setTimeout(run, 0);
      return null;
    },

    /* Unchosen free tin before payment (audit, 26 Oct 2026). With a code active,
     * 3+ paid tins and no free tin picked, a tap on Checkout or a wallet button
     * first asks: Choose My Free Tin, or Continue Without A Free Tin. Wallets
     * render in iframes, so a transparent cover over them catches the tap. */
    refPending: function () {
      return !!(this.offerRef && this.offerGiftOnly() && this.offerDead() !== this.offerToken &&
        this.giftLines(this.cart || {}).length < this.giftsAllowed() && this._refSkip !== this.offerToken);
    },

    renderRefPending: function () {
      var foot = this.el.querySelector('[data-eu-cart-foot]');
      if (!foot) return;
      var pending = this.refPending();
      this.el.setAttribute('data-eu-ref-pending', String(pending));
      var form = foot.querySelector('.eu-cart__checkoutform');
      var block = foot.querySelector('[data-eu-refpend]');
      if (!pending || !this._refAsk) { if (block) block.remove(); if (!pending) this._refAsk = false; return; }
      if (!block && form) {
        block = document.createElement('div');
        block.className = 'eu-refpend';
        block.setAttribute('data-eu-refpend', '');
        form.parentNode.insertBefore(block, form);
      }
      if (block) block.innerHTML = '<b>Choose your free flavour before checkout.</b>' +
        '<div class="eu-refpend__row">' +
          '<button type="button" class="eu-refpend__go" data-eu-refpend-go>Choose My Free Tin</button>' +
          '<button type="button" class="eu-refpend__skip" data-eu-refpend-skip>Continue Without A Free Tin</button>' +
        '</div>';
    },

    groupHtml: function (g) {
      var qty = g.lines.reduce(function (a, l) { return a + l.quantity; }, 0);
      /* Label from the live count, not the Bundle property stamped at add time —
       * upgrading a 3-pack in place would otherwise leave the header saying
       * "3 Tins" above five tins. */
      g.label = 'Your Bundle — ' + qty + (qty === 1 ? ' Tin' : ' Tins');
      /* What this bundle costs, after everything — including a store-wide
       * automatic discount, which lands on the lines but not on
       * final_line_price. Reading final_line_price alone printed $359.40 on a
       * 12-tin bundle whose total, four rows below, said $287.52. */
      var now = this.groupNet(this.cart, g.id);
      var was = g.lines.reduce(function (a, l) { return a + l.original_line_price; }, 0);
      /* From the catalogue, which carries custom.pouches_per_tin. This used to
       * scrape "Quantity: 15 Pouches" out of the description prose, which meant
       * the drawer reported a stale number the moment the metafield changed and
       * the marketing copy didn't. The metafield is the authority. */
      var self = this;
      var pouches = g.lines.reduce(function (a, l) {
        var t = self.tinByVariant(l.id);
        return a + (t ? t.pouches * l.quantity : 0);
      }, 0);

      /* The title already says "5 Tins", so repeating it here is noise. The
       * saving belongs next to the bundle it applies to, which makes the
       * collapsed card self-contained. */
      var sub = pouches ? pouches + ' pouches' : qty + (qty === 1 ? ' tin' : ' tins');
      if (was > now) sub += ' · You\u2019re Saving ' + money(was - now);

      /* Collapsed by default.
       *
       * Expanded, a 10-tin bundle is ten rows the customer has to scroll past
       * before the upgrade card exists — which buries the most valuable thing
       * the cart can say. A thumbnail strip gives enough recognition to confirm
       * the order is right, and the full list is one tap away for anyone who
       * wants to check every flavour. Summarise the bundle; don't dump it. */
      var open = !!this._openGroups[g.id];

      var thumbs = g.lines.map(function (l) {
        var img = l.image ? l.image.replace(/(\.[a-z]+)(\?|$)/i, '_80x$1$2') : '';
        var n = l.quantity > 1 ? '<i>' + l.quantity + '</i>' : '';
        return '<span class="eu-bg__thumb">' +
          (img ? '<img src="' + esc(img) + '" alt="" loading="lazy" width="34" height="34">' : '') +
          n + '</span>';
      }).join('');

      return '<div class="eu-bundle-group" data-open="' + open + '">' +
        '<div class="eu-bundle-group__head">' +
          '<span><span class="eu-bundle-group__name">' + esc(g.label) + '</span>' +
            '<span class="eu-bundle-group__sub">' + sub + '</span></span>' +
          '<span class="eu-bundle-group__price">' + money(now) +
            (was > now ? '<span class="eu-bundle-group__was">' + money(was) + '</span>' : '') +
          '</span>' +
        '</div>' +

        (open ? '' : '<div class="eu-bg__thumbs">' + thumbs + '</div>') +

        '<button type="button" class="eu-bg__toggle" data-eu-group-toggle="' + esc(g.id) + '"' +
          ' aria-expanded="' + open + '">' +
          (open ? 'Hide bundle details' : 'View bundle details') +
          '<i aria-hidden="true">' + (open ? '&#9652;' : '&#9662;') + '</i>' +
        '</button>' +

        (open
          ? '<div class="eu-bundle-group__items">' + g.lines.map(this.lineHtml, this).join('') +
              '<button type="button" class="eu-bundle-group__remove" data-eu-remove-group="' +
                esc(g.id) + '">Remove bundle</button>' +
            '</div>'
          : '') +
      '</div>';
    },

    lineHtml: function (l) {
      var self0 = this;
      var inBundle = !!(l.properties || {})._bundle_id;
      var isGift = !!(l.properties || {})._reward;
      // "BERSERKER - Valhalla Vanilla (100mg)" is too long for a nested row;
      // the group is already one brand-mixed bundle, so lead with the flavour.
      var flavour = String(l.product_title || '').split(' - ').pop().split(' (')[0].trim();
      var brand = String(l.product_title || '').split(' - ')[0].trim();
      var img = l.image ? l.image.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2') : '';
      var saved = l.original_line_price - l.final_line_price;

      if (isGift) {
        // Which offer is paying for the tin RIGHT NOW, not which one was in play
        // when it was picked: crossing $219 (or dropping back under it) hands the
        // tin from the email link to the spend reward or back (review, 6 Oct
        // 2026). relabelGift keeps the stored 'Cart Reward' text in step.
        var tag = self0.offerGiftOnly() ? (self0.offerRef === 'friend' ? 'From A Mate' : self0.offerRef === 'reward' ? 'Your Reward' : 'From us') : 'Cart Reward';
        return '<div class="eu-item eu-item--gift">' +
          (img ? '<img class="eu-item__img" src="' + esc(img) + '" alt="" loading="lazy" width="62" height="62">'
               : '<span class="eu-item__img"></span>') +
          '<span class="eu-item__body">' +
            '<span class="eu-item__title">' + esc(flavour) + '</span>' +
            '<span class="eu-item__meta">' + esc(brand) + '</span>' +
            '<span class="eu-item__reward">' + tag + '</span>' +
            '<span style="display:flex;gap:16px;align-items:center;flex-wrap:wrap">' +
            (self0.offerGiftOnly() && self0.offerRef
              ? '<button type="button" class="eu-item__remove" style="margin:0" data-eu-gift-change="' + esc(l.key) + '">Change Flavour</button>'
              : '') +
            '<button type="button" class="eu-item__remove" style="margin:0" data-eu-gift-remove="' + esc(l.key) + '">Remove</button>' +
            '</span>' +
          '</span>' +
          '<span class="eu-item__prices">' +
            '<span class="eu-item__free">FREE</span>' +
            '<span class="eu-item__was">' + money(l.original_line_price) + '</span>' +
          '</span>' +
        '</div>';
      }

      return '<div class="eu-item">' +
        (img ? '<img class="eu-item__img" src="' + esc(img) + '" alt="" loading="lazy" width="62" height="62">'
             : '<span class="eu-item__img"></span>') +
        '<span class="eu-item__body">' +
          /* Flavour first, brand underneath — the same treatment as bundle and
           * reward rows. Loose lines used to print the raw product title
           * ("BERSERKER - Eagles Blood (100mg)"), so one drawer showed three
           * different ways of naming the same kind of thing. */
          '<span class="eu-item__title">' + esc(flavour) + '</span>' +
          (inBundle
            ? '<span class="eu-item__meta">' + esc(brand) + ' &nbsp;·&nbsp; ×' + l.quantity + '</span>' +
              (function () {
                /* Only visible once the customer expands the bundle, so the
                 * height is free — and "Winterberry, BERSERKER, x1" was too
                 * thin to check an order against. */
                var t = self0.tinByVariant(l.id);
                if (!t) return '';
                var band = strengthBand(t.caffeine);
                var bits = [];
                if (band) bits.push(esc(band.label) + (t.caffeine ? ' · ' + t.caffeine + 'mg' : ''));
                if (t.pouches) bits.push(t.pouches + ' Pouches');
                return bits.length
                  ? '<span class="eu-item__spec">' + bits.join(' &nbsp;·&nbsp; ') + '</span>'
                  : '';
              })()
            : '<span class="eu-item__meta">' + esc(brand) + '</span>' +
              '<span class="eu-qty">' +
                '<button type="button" data-eu-step="-1" data-eu-key="' + esc(l.key) + '" aria-label="Decrease">−</button>' +
                '<span class="eu-qty__n">' + l.quantity + '</span>' +
                '<button type="button" data-eu-step="1" data-eu-key="' + esc(l.key) + '" aria-label="Increase">+</button>' +
              '</span>' +
              '<button type="button" class="eu-item__remove" data-eu-remove="' + esc(l.key) + '">Remove</button>') +
        '</span>' +
        (inBundle ? '' :
          '<span class="eu-item__prices">' +
            '<span class="eu-item__price">' + money(l.final_line_price) + '</span>' +
            (saved > 0 ? '<span class="eu-item__was">' + money(l.original_line_price) + '</span>' +
                         '<span class="eu-item__save">Save ' + money(saved) + '</span>' : '') +
          '</span>') +
      '</div>';
    }
  };

  Cart.track = track;
  window.EUCart = Cart;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { Cart.mount(); });
  else Cart.mount();
})();
